
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useAdminClientProfileQuery,
  useAdminClientChecklistQuery,
  useAdminClientFilesQuery,
  useGoalMetricsCatalogQuery,
  useAdminClientEnabledGoalMetricsQuery,
} from "@/components/trainer/use-client-profile-cached-queries";
import { queryKeys } from "@/lib/query-keys";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  ArrowLeft,
  Trophy,
  Target,
  Calendar,
  CalendarDays,
  Clock,
  Plus,
  MessageSquare,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  User,
  Activity,
  Zap,
  FileText,
  Download,
  Image as ImageIcon,
  Upload,
  Dumbbell,
  CheckCircle2,
  Search,
  X,
  Edit,
  Trash2,
} from "lucide-react";
import { updateClientOnboarding, type ClientProfileData } from "@/integrations/backend/admin-users";
import { listWorkoutAssigned, deleteWorkoutAssigned, type WorkoutAssigned } from "@/integrations/backend/workout-assigned";
import { toast } from "sonner";
import { listNotes, createNote, updateNote, deleteNote, type Note as NoteType } from "@/integrations/backend/notes";
import { getClientPrograms, type ClientProgram } from "@/integrations/backend/programs";
import type { OnboardingData } from "@/integrations/backend/onboarding";
import {
  adminDeleteClientFile,
  adminUploadClientFile,
  type ClientFileDto,
} from "@/integrations/backend/client-files";
import {
  completeClientChecklistItem,
  incompleteClientChecklistItem,
  type ChecklistItemToProfile,
} from "@/integrations/backend/checklist-items";

type OnboardingSummaryView = {
  gender: string | null;
  dob: string | null;
  height: {
    unit: "imperial" | "metric" | null;
    feet: number | null;
    inches: number | null;
    cm: number | null;
  };
  weight: {
    unit: "lb" | "kg" | null;
    value: number | null;
  };
  motivations: string[];
  workoutFrequency: string | null;
  workoutDuration: string | null;
  exerciseTypes: string[];
  sportsEnjoyed: string | null;
  variety: string | null;
  timeline: string | null;
  additionalServices: string[];
  trainingForEvent: string | null;
  eventDetails: string | null;
  eventDate: string | null;
  activityLevel: string | null;
};

const emptyOnboardingSummary: OnboardingSummaryView = {
  gender: null,
  dob: null,
  height: { unit: null, feet: null, inches: null, cm: null },
  weight: { unit: null, value: null },
  motivations: [],
  workoutFrequency: null,
  workoutDuration: null,
  exerciseTypes: [],
  sportsEnjoyed: null,
  variety: null,
  timeline: null,
  additionalServices: [],
  trainingForEvent: null,
  eventDetails: null,
  eventDate: null,
  activityLevel: null,
};

// Helper function to transform backend API format to OnboardingSummary format
function onboardingDataToSummary(data: OnboardingData): OnboardingSummaryView {
  return {
    gender: data.gender ?? null,
    dob: data.dob ?? null,
    height: {
      unit: (data.heightUnit as "imperial" | "metric" | null | undefined) ?? null,
      feet:
        data.heightFeet !== null && data.heightFeet !== undefined
          ? typeof data.heightFeet === "number"
            ? data.heightFeet
            : Number(data.heightFeet)
          : null,
      inches:
        data.heightInches !== null && data.heightInches !== undefined
          ? typeof data.heightInches === "number"
            ? data.heightInches
            : Number(data.heightInches)
          : null,
      cm:
        data.heightCm !== null && data.heightCm !== undefined
          ? typeof data.heightCm === "number"
            ? data.heightCm
            : Number(data.heightCm)
          : null,
    },
    weight: {
      unit: (data.weightUnit as "lb" | "kg" | null | undefined) ?? null,
      value:
        data.bodyWeight !== null && data.bodyWeight !== undefined
          ? typeof data.bodyWeight === "number"
            ? data.bodyWeight
            : Number(data.bodyWeight)
          : null,
    },
    motivations: Array.isArray(data.motivation) ? data.motivation : [],
    workoutFrequency: data.workoutFrequency ?? null,
    workoutDuration: data.workoutDuration ?? null,
    exerciseTypes: Array.isArray(data.exerciseTypes) ? data.exerciseTypes : [],
    sportsEnjoyed: data.sportsEnjoyed ?? null,
    variety: data.variety ?? null,
    timeline: data.resultTimeframe ?? null,
    additionalServices: Array.isArray(data.additionalServices) ? data.additionalServices : [],
    trainingForEvent: data.trainingForEvent ?? null,
    eventDetails: data.eventDetails ?? null,
    eventDate: data.eventDate ?? null,
    activityLevel: data.activityLevel ?? null,
  };
}
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import VarietyField from "@/components/variety-field";
import DobPicker from "@/components/ui/dob-picker";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useEquipment } from "@/hooks/use-equipment";
import { cn } from "@/lib/utils";
import { OnboardingSummary } from "@/lib/schemas";
import {
  additionalServiceOptions,
  activityLevelOptions,
  exerciseTypeOptions,
  genderOptions,
  labels,
  motivationOptions,
  timeframeOptions,
  workoutDurationOptions,
  workoutFrequencyOptions,
} from "@/lib/onboarding-options";
import { format, isValid as isValidDate, parse, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, getDay } from "date-fns";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Area, CartesianGrid, ComposedChart, Line, ReferenceLine, XAxis, YAxis } from "recharts";
import type { DateRange } from "react-day-picker";
import { Calendar as DateRangeCalendar } from "@/components/ui/calendar";
import { Progress } from "@/components/ui/progress";
import AssignWorkoutDialog from "./AssignWorkoutDialog";
import AssignProgramDialog from "./AssignProgramDialog";
import EditAssignedProgramDialog from "./EditAssignedProgramDialog";
import EditAssignedWorkoutWithAIDialog from "./EditAssignedWorkoutWithAIDialog";
import AdminChat from "./AdminChat";
import ClientProgramTabContent from "./ClientProgramTabContent";
import WorkoutGeneratorPage from "./WorkoutGeneratorPage";
import {
  completeDateRangeToFilterISO,
  formatGoalDateRangeLabel,
  formatISODateLabelShort,
  parseYYYYMMDDToLocalDate,
} from "@/lib/date-only";
import {
  adminCreateBodyWeight,
  adminCreateRestingHr,
  adminGetGoalsSummary,
  adminSetEnabledGoalMetrics,
  adminListOptionalMetricEntries,
  adminCreateOptionalMetricEntry,
  adminListBodyWeight,
  adminListRestingHr,
  adminListRpe,
  type GoalMetricCatalogItem,
  type OptionalMetricEntry,
  type BodyWeightEntry,
  type GoalsSummary,
  type RestingHrEntry,
  type RpeEntry,
} from "@/integrations/backend/goals";

interface ClientProfileViewProps {
  clientId: string;
  onBack: () => void;
  onAssignWorkout?: (clientId: string, assignmentDate: Date | null, assignedWorkoutId?: string) => void;
  onEditWorkout?: (assignedWorkoutId: string, clientId: string, assignmentDate: Date | null) => void;
  onDeleteWorkout?: (assignmentId: string) => void;
  initialTab?: string;
  initialWorkoutView?: "history" | "scheduled";
}

const CLIENT_PROFILE_TABS = new Set([
  "overview",
  "checklist",
  "onboarding",
  "program",
  "workout-generator",
  "workouts",
  "equipment",
  "progress",
  "files",
  "notes",
  "chat",
]);

function normalizeClientProfileTab(tab: string | null | undefined): string | null {
  if (!tab) return null;
  return CLIENT_PROFILE_TABS.has(tab) ? tab : null;
}

const ClientProfileView = ({ clientId, onBack, onAssignWorkout, onEditWorkout, onDeleteWorkout, initialTab = "overview", initialWorkoutView = "history" }: ClientProfileViewProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profileQuery = useAdminClientProfileQuery(clientId);
  const checklistQuery = useAdminClientChecklistQuery(clientId);
  const filesQuery = useAdminClientFilesQuery(clientId);
  const goalCatalogQuery = useGoalMetricsCatalogQuery();
  const enabledMetricsQuery = useAdminClientEnabledGoalMetricsQuery(clientId);

  const [searchParams, setSearchParams] = useSearchParams();
  
  // Real client profile data
  const [clientProfileData, setClientProfileData] = useState<ClientProfileData | null>(null);
  const [clientProfileLoading, setClientProfileLoading] = useState(true);
  const [clientProfileError, setClientProfileError] = useState<string | null>(null);
  
  // Workout assignments data
  const [workoutAssignments, setWorkoutAssignments] = useState<WorkoutAssigned[]>([]);
  const [loadingWorkouts, setLoadingWorkouts] = useState(true);
  
  // Initialize from URL params, fallback to props for backward compatibility
  const getInitialTab = () => {
    // In AdminPortal, the top-level tab is `tab=clients` and the nested client view uses `clientTab=...`.
    // Never treat `tab=clients` (or any other unknown value) as a client-profile subtab.
    const urlClientTab = normalizeClientProfileTab(searchParams.get("clientTab"));
    if (urlClientTab) return urlClientTab;

    const urlTab = normalizeClientProfileTab(searchParams.get("tab"));
    if (urlTab) return urlTab;

    return normalizeClientProfileTab(initialTab) ?? "overview";
  };
  
  const getInitialWorkoutView = (): "history" | "scheduled" => {
    const urlView = searchParams.get("workoutView");
    if (urlView === "scheduled" || urlView === "history") {
      return urlView;
    }
    return initialWorkoutView;
  };
  
  const [workoutView, setWorkoutView] = useState<"history" | "scheduled">(getInitialWorkoutView());
  const [activeTab, setActiveTab] = useState<string>(getInitialTab());
  const [workoutDisplayMode, setWorkoutDisplayMode] = useState<"list" | "calendar">("list");

  // Safety: if anything sets an invalid tab (e.g. `tab=clients`), coerce back to a valid default.
  useEffect(() => {
    if (!CLIENT_PROFILE_TABS.has(activeTab)) {
      setActiveTab("overview");
    }
  }, [activeTab]);

  // Sync activeTab with URL
  useEffect(() => {
    const isNestedClientView =
      searchParams.get("tab") === "clients" || searchParams.has("clientId") || searchParams.has("clientTab");

    const currentTab = isNestedClientView
      ? normalizeClientProfileTab(searchParams.get("clientTab"))
      : normalizeClientProfileTab(searchParams.get("tab"));
    if (currentTab !== activeTab) {
      const newParams = new URLSearchParams(searchParams);
      // Update clientTab param (used by AdminPortal) or tab param (if standalone)
      if (isNestedClientView) {
        newParams.set("clientTab", activeTab);
      } else {
        newParams.set("tab", activeTab);
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  // Sync workoutView with URL
  useEffect(() => {
    const currentView = searchParams.get("workoutView");
    if (currentView !== workoutView) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("workoutView", workoutView);
      setSearchParams(newParams, { replace: true });
    }
  }, [workoutView, searchParams, setSearchParams]);

  // Update tab and workout view when props change (e.g., when navigating back from workout builder)
  useEffect(() => {
    const urlTab =
      normalizeClientProfileTab(searchParams.get("clientTab")) ??
      normalizeClientProfileTab(searchParams.get("tab"));
    if (!urlTab && initialTab !== activeTab) {
      setActiveTab(normalizeClientProfileTab(initialTab) ?? "overview");
    }
  }, [initialTab, activeTab, searchParams]);

  useEffect(() => {
    const urlView = searchParams.get("workoutView");
    if (!urlView && initialWorkoutView !== workoutView) {
      setWorkoutView(initialWorkoutView);
    }
  }, [initialWorkoutView, workoutView, searchParams]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [assignWorkoutDialogOpen, setAssignWorkoutDialogOpen] = useState(false);
  const [deleteWorkoutDialogOpen, setDeleteWorkoutDialogOpen] = useState(false);
  const [workoutToDelete, setWorkoutToDelete] = useState<{ id: string; name: string } | null>(null);
  const [editWorkoutWithAIOpen, setEditWorkoutWithAIOpen] = useState(false);
  const [workoutToEditWithAI, setWorkoutToEditWithAI] = useState<WorkoutAssigned | null>(null);
  
  useEffect(() => {
    if (profileQuery.data) setClientProfileData(profileQuery.data);
  }, [profileQuery.data]);

  useEffect(() => {
    setClientProfileLoading(profileQuery.isPending);
    setClientProfileError(
      profileQuery.error ? String((profileQuery.error as Error)?.message || profileQuery.error) : null
    );
  }, [profileQuery.isPending, profileQuery.error]);

  useEffect(() => {
    if (checklistQuery.data) {
      const sorted = [...checklistQuery.data].sort((a, b) => a.checklist_item.order - b.checklist_item.order);
      setChecklistItems(sorted);
    }
  }, [checklistQuery.data]);

  useEffect(() => {
    setChecklistLoading(checklistQuery.isPending);
  }, [checklistQuery.isPending]);

  // Handler to complete a checklist item
  const handleCompleteChecklistItem = async (itemId: string) => {
    try {
      setCompletingItemId(itemId);
      const updated = await completeClientChecklistItem(clientId, itemId);
      setChecklistItems(prev => 
        prev.map(item => item.id === itemId ? updated : item)
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.clients.checklistItems(clientId) });
    } catch (error) {
      console.error("Failed to complete checklist item", error);
    } finally {
      setCompletingItemId(null);
    }
  };

  // Handler to mark a checklist item as incomplete
  const handleIncompleteChecklistItem = async (itemId: string) => {
    try {
      setCompletingItemId(itemId);
      const updated = await incompleteClientChecklistItem(clientId, itemId);
      setChecklistItems(prev => 
        prev.map(item => item.id === itemId ? updated : item)
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.clients.checklistItems(clientId) });
    } catch (error) {
      console.error("Failed to mark checklist item as incomplete", error);
    } finally {
      setCompletingItemId(null);
    }
  };

  const refreshWorkoutAssignments = useCallback(async () => {
    try {
      setLoadingWorkouts(true);
      const assignments = await listWorkoutAssigned(clientId);
      setWorkoutAssignments(assignments);
    } catch (error) {
      console.error("Failed to load workout assignments", error);
      setWorkoutAssignments([]);
    } finally {
      setLoadingWorkouts(false);
    }
  }, [clientId]);

  // Fetch workout assignments on mount and when clientId changes
  useEffect(() => {
    void refreshWorkoutAssignments();
  }, [refreshWorkoutAssignments]);
  
  const clientData = {
    profile: clientProfileData?.profile || {
      id: clientId,
      first_name: null,
      last_name: null,
      email: null
    },
    onboarding: {
      // High-level motivations and goals
      motivations: ["Weight Loss", "Strength Building", "Mental Health"],
      goals: ["Lose 15 lbs", "Build Muscle", "Improve Endurance"],
      // Preferences
      workoutFrequency: "3 times per week",
      fitnessLevel: "Intermediate",
      location: "Gym",
      exerciseTypes: ["strength-training", "cardio", "hiit"],
      // Health
      healthConditions: "None",
      injuries: "Previous knee injury (resolved)",
      // Success metrics structure
      successMetrics: ["body-weight", "energy-level", "strength"],
      currentMetrics: {
        "body-weight": "180 lbs",
        "energy-level": "moderate",
        "strength": "Bench press 155 lbs"
      },
      specificGoals: {
        "body-weight": "165 lbs",
        "energy-level": "consistent",
        "strength": "Bench press 200 lbs"
      },
      // Persona & timeframe
      gymPersona: ["form-focused", "data-tracker"],
      resultTimeframe: "3-months",
      // Services & plan
      additionalServices: ["find-gym", "equipment"],
      selectedPlan: "coach-plan",
      billingCycle: "monthly",

      // Backwards-compatibility with older dummy keys used in UI
      fitness_goals: ["Lose 15 lbs", "Build Muscle", "Improve Endurance"],
      current_fitness_level: "Intermediate",
      workout_frequency: "4-5 times per week",
      preferred_location: "Gym",
      health_conditions: ["None"],
      specific_goals: "I want to lose 15 pounds over the next 3 months while building lean muscle mass. My main focus is on improving my overall health and feeling more confident in my body."
    },
    workoutSessions: [
      {
        id: "1",
        workout_name: "Upper Body Strength",
        workout_type: "Strength Training",
        duration_minutes: 52,
        calories_burned: 380,
        points_earned: 320,
        completed_at: new Date().toISOString(),
        intensity_rating: 8,
        energy_level: "ready",
        energy_rating: 7,
        exercise_sets: [
          {
            id: "1",
            exercise_name: "Bench Press",
            sets: 4,
            reps: 8,
            weight_lbs: 185
          },
          {
            id: "2",
            exercise_name: "Incline Dumbbell Press",
            sets: 3,
            reps: 10,
            weight_lbs: 65
          },
          {
            id: "3",
            exercise_name: "Pull-ups",
            sets: 3,
            reps: 8,
            weight_lbs: null
          },
          {
            id: "4",
            exercise_name: "Barbell Rows",
            sets: 4,
            reps: 10,
            weight_lbs: 135
          }
        ]
      },
      {
        id: "2",
        workout_name: "HIIT Cardio",
        workout_type: "Cardio",
        duration_minutes: 35,
        calories_burned: 450,
        points_earned: 280,
        completed_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        intensity_rating: 9,
        energy_level: "supercharged",
        energy_rating: 9,
        exercise_sets: [
          {
            id: "5",
            exercise_name: "Burpees",
            sets: 5,
            reps: 15,
            weight_lbs: null
          },
          {
            id: "6",
            exercise_name: "Mountain Climbers",
            sets: 4,
            reps: 20,
            weight_lbs: null
          },
          {
            id: "7",
            exercise_name: "Jump Squats",
            sets: 4,
            reps: 15,
            weight_lbs: null
          }
        ]
      },
      {
        id: "3",
        workout_name: "Lower Body Power",
        workout_type: "Strength Training",
        duration_minutes: 48,
        calories_burned: 320,
        points_earned: 300,
        completed_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
        intensity_rating: 7,
        energy_level: "tired",
        energy_rating: 5,
        exercise_sets: [
          {
            id: "8",
            exercise_name: "Squats",
            sets: 4,
            reps: 12,
            weight_lbs: 225
          },
          {
            id: "9",
            exercise_name: "Romanian Deadlifts",
            sets: 3,
            reps: 10,
            weight_lbs: 185
          },
          {
            id: "10",
            exercise_name: "Leg Press",
            sets: 3,
            reps: 15,
            weight_lbs: 320
          }
        ]
      },
      {
        id: "4",
        workout_name: "Full Body Circuit",
        workout_type: "Circuit Training",
        duration_minutes: 42,
        calories_burned: 395,
        points_earned: 340,
        completed_at: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
        intensity_rating: 6,
        energy_level: "low",
        energy_rating: 6,
        exercise_sets: [
          {
            id: "11",
            exercise_name: "Push-ups",
            sets: 3,
            reps: 15,
            weight_lbs: null
          },
          {
            id: "12",
            exercise_name: "Kettlebell Swings",
            sets: 4,
            reps: 20,
            weight_lbs: 35
          },
          {
            id: "13",
            exercise_name: "Plank",
            sets: 3,
            reps: 60,
            weight_lbs: null
          }
        ]
      }
    ],
    achievements: [
      {
        id: "1",
        achievement_name: "First Milestone",
        description: "Completed your first 10 workouts",
        points_value: 100,
        earned_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        id: "2",
        achievement_name: "Consistency Champion",
        description: "Completed workouts 5 days in a row",
        points_value: 150,
        earned_at: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        id: "3",
        achievement_name: "Strength Builder",
        description: "Increased bench press by 20 lbs",
        points_value: 200,
        earned_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        id: "4",
        achievement_name: "Cardio King",
        description: "Completed 25 cardio sessions",
        points_value: 175,
        earned_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
      },
      {
        id: "5",
        achievement_name: "Weight Loss Warrior",
        description: "Lost 10 pounds",
        points_value: 250,
        earned_at: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString()
      }
    ],
    // Progress updates: records of metric values over time
    progressUpdates: [
      {
        id: "p1",
        date: new Date(Date.now() - 20 * 24 * 60 * 60 * 1000).toISOString(),
        metrics: {
          "body-weight": "182 lbs",
          "tshirt-size": "XL",
          "strength": "Bench 165 lbs",
        },
      },
      {
        id: "p2",
        date: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        metrics: {
          "body-weight": "176 lbs",
          "tshirt-size": "L",
          "strength": "Bench 185 lbs",
        },
      },
      {
        id: "p3",
        date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        metrics: {
          "body-weight": "172 lbs",
          "tshirt-size": "L",
          "strength": "Bench 195 lbs",
        },
      },
    ],
    assignments: [
      {
        id: "1",
        workout_name: "Upper Body Focus",
        workout_description: "Strength training focused on chest, shoulders, and arms with progressive overload",
        assigned_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        completed_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        status: "completed",
        exercises: [
          { id: "a1", exercise_name: "Bench Press", sets: 4, reps: 8, weight_lbs: 155 },
          { id: "a2", exercise_name: "Incline DB Press", sets: 3, reps: 10, weight_lbs: 60 },
          { id: "a3", exercise_name: "Pull-ups", sets: 3, reps: 8 }
        ]
      },
      {
        id: "2",
        workout_name: "HIIT Conditioning",
        workout_description: "High-intensity interval training to improve cardiovascular endurance",
        assigned_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
        completed_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        status: "completed",
        exercises: [
          { id: "a4", exercise_name: "Burpees", sets: 5, reps: 15 },
          { id: "a5", exercise_name: "Mountain Climbers", sets: 4, reps: 20 },
          { id: "a6", exercise_name: "Jump Squats", sets: 4, reps: 15 }
        ]
      },
      {
        id: "3",
        workout_name: "Lower Body Strength",
        workout_description: "Compound movements targeting legs and glutes for maximum strength gains",
        assigned_at: new Date().toISOString(),
        completed_at: null,
        status: "assigned",
        exercises: [
          { id: "a7", exercise_name: "Back Squat", sets: 4, reps: 8, weight_lbs: 225 },
          { id: "a8", exercise_name: "Romanian Deadlift", sets: 3, reps: 10, weight_lbs: 185 },
          { id: "a9", exercise_name: "Walking Lunges", sets: 3, reps: 12, weight_lbs: 40 }
        ]
      },
      {
        id: "4",
        workout_name: "Core & Flexibility",
        workout_description: "Core strengthening exercises combined with mobility work",
        assigned_at: new Date().toISOString(),
        completed_at: null,
        status: "assigned",
        exercises: [
          { id: "a10", exercise_name: "Plank", sets: 3, reps: 60 },
          { id: "a11", exercise_name: "Dead Bug", sets: 3, reps: 12 },
          { id: "a12", exercise_name: "Hip Flexor Stretch", sets: 2, reps: 60 }
        ]
      }
    ]
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return parseYYYYMMDDToLocalDate(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Extract data from clientData (achievements, progressUpdates still use dummy data)
  const { achievements, progressUpdates } = clientData;

  // Files state (admin only)
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState("");
  const [clientFiles, setClientFiles] = useState<ClientFileDto[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [filesUploading, setFilesUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<ClientFileDto | null>(null);

  const refreshClientFiles = async () => {
    setFilesError(null);
    await queryClient.invalidateQueries({ queryKey: queryKeys.admin.clients.files(clientId) });
  };

  useEffect(() => {
    if (filesQuery.data) setClientFiles(filesQuery.data);
  }, [filesQuery.data]);

  useEffect(() => {
    setFilesLoading(filesQuery.isPending);
  }, [filesQuery.isPending]);

  useEffect(() => {
    if (filesQuery.error) setFilesError(String(filesQuery.error));
    else setFilesError(null);
  }, [filesQuery.error]);

  // Admin-side onboarding summary state (from API)
  const [onboardingSummary, setOnboardingSummary] = useState<OnboardingSummaryView>(emptyOnboardingSummary);
  const [onboardingEdit, setOnboardingEdit] = useState(false);
  const [form, setForm] = useState({
    gender: "",
    dob: "",
    heightUnit: "imperial" as "imperial" | "metric",
    heightFeet: "",
    heightInches: "",
    heightCm: "",
    weightUnit: "lb" as "lb" | "kg",
    weightValue: "",
    motivations: [] as string[],
    activityLevel: "",
    workoutFrequency: "",
    workoutDuration: "",
    exerciseTypes: [] as string[],
    sportsEnjoyed: "",
    variety: "",
    timeline: "",
    services: [] as string[],
    trainingForEvent: "",
    eventDetails: "",
    eventDate: "",
  });

  // Equipment tab state (from API)
  const { data: equipment, loading: equipmentLoading } = useEquipment();
  const [equipmentEdit, setEquipmentEdit] = useState(false);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<string[]>([]);
  const [workoutLocations, setWorkoutLocations] = useState<string[]>([]);
  const [locationEdit, setLocationEdit] = useState(false);
  const [savingEquipment, setSavingEquipment] = useState(false);
  const [savingOnboarding, setSavingOnboarding] = useState(false);

  // Map backend location codes to display text
  const locationCodeToDisplay = (code: string): string => {
    const mapping: Record<string, string> = {
      "home": "At Home",
      "gym": "Commercial Gym",
      "outdoor": "Outdoor / Park",
      "mixed": "Mixed",
    };
    return mapping[code] || code;
  };

  // Map display text to backend codes
  const locationDisplayToCode = (display: string | null): string | null => {
    if (!display) return null;
    const mapping: Record<string, string> = {
      "At Home": "home",
      "Building Gym": "gym",
      "Commercial Gym": "gym",
      "Outdoor / Park": "outdoor",
      "Mixed": "mixed",
    };
    return mapping[display] || display.toLowerCase();
  };

  const locationCodesToDisplayList = (codes: string[] | null | undefined): string[] => {
    if (!codes || codes.length === 0) return [];
    return codes.map(locationCodeToDisplay);
  };

  // Load onboarding and equipment data from API when clientProfileData is loaded
  useEffect(() => {
    if (!clientProfileData) return;
    
    // Transform onboarding data
    const summary = onboardingDataToSummary(clientProfileData.onboarding);
    setOnboardingSummary(summary);
    
    // Set form from onboarding data
    setForm({
      gender: summary.gender || "",
      dob: summary.dob || "",
      heightUnit: summary.height.unit || "imperial",
      heightFeet: String(summary.height.feet ?? ""),
      heightInches: String(summary.height.inches ?? ""),
      heightCm: String(summary.height.cm ?? ""),
      weightUnit: summary.weight.unit || "lb",
      weightValue: String(summary.weight.value ?? ""),
      motivations: [...summary.motivations],
      activityLevel: summary.activityLevel || "",
      workoutFrequency: summary.workoutFrequency || "",
      workoutDuration: summary.workoutDuration || "",
      exerciseTypes: [...summary.exerciseTypes],
      sportsEnjoyed: summary.sportsEnjoyed || "",
      variety: summary.variety || "",
      timeline: summary.timeline || "",
      services: [...summary.additionalServices],
      trainingForEvent: summary.trainingForEvent || "",
      eventDetails: summary.eventDetails || "",
      eventDate: summary.eventDate || "",
    });
    
    // Set equipment IDs and location
    if (clientProfileData.onboarding.equipmentIds) {
      setSelectedEquipmentIds(clientProfileData.onboarding.equipmentIds);
    }
    setWorkoutLocations(locationCodesToDisplayList(clientProfileData.onboarding.location));
  }, [clientProfileData]);

  const saveOnboarding = async () => {
    try {
      setSavingOnboarding(true);

      const payload: Partial<OnboardingData> = {
        gender: form.gender || null,
        dob: form.dob || null,
        heightUnit: form.heightUnit || null,
        heightFeet: form.heightUnit === "imperial" ? (form.heightFeet ? Number(form.heightFeet) : null) : null,
        heightInches: form.heightUnit === "imperial" ? (form.heightInches ? Number(form.heightInches) : null) : null,
        heightCm: form.heightUnit === "metric" ? (form.heightCm ? Number(form.heightCm) : null) : null,
        bodyWeight: form.weightValue ? Number(form.weightValue) : null,
        weightUnit: form.weightUnit || null,
        motivation: Array.isArray(form.motivations) ? form.motivations.slice(0, 2) : [],
        activityLevel: form.activityLevel || null,
        workoutFrequency: form.workoutFrequency || null,
        workoutDuration: form.workoutDuration || null,
        exerciseTypes: Array.isArray(form.exerciseTypes) ? form.exerciseTypes : [],
        sportsEnjoyed: form.exerciseTypes?.includes("sports") ? (form.sportsEnjoyed?.trim() ? form.sportsEnjoyed.trim() : null) : null,
        variety: form.variety?.trim() ? form.variety.trim() : "",
        resultTimeframe: form.timeline || null,
        additionalServices: Array.isArray(form.services) ? form.services : [],
        trainingForEvent: form.trainingForEvent || null,
        eventDetails: form.eventDetails || null,
        eventDate: form.eventDate || null,
      };

      const updatedOnboarding = await updateClientOnboarding(clientId, payload);
      toast.success("Onboarding updated");

      setClientProfileData((prev) => {
        if (!prev) return prev;
        return { ...prev, onboarding: updatedOnboarding };
      });
      setOnboardingSummary(onboardingDataToSummary(updatedOnboarding));
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.clients.profile(clientId) });
    } catch (error: any) {
      console.error("Failed to save onboarding", error);
      toast.error(error?.message ?? "Failed to save onboarding");
      throw error;
    } finally {
      setSavingOnboarding(false);
    }
  };

  const saveEquipmentPrefs = async (ids: string[]) => {
    try {
      setSavingEquipment(true);
      const updatedOnboarding = await updateClientOnboarding(clientId, { equipmentIds: ids });
      setSelectedEquipmentIds(updatedOnboarding.equipmentIds || []);
      setClientProfileData((prev) => {
        if (!prev) return prev;
        return { ...prev, onboarding: updatedOnboarding };
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.clients.profile(clientId) });
    } catch (error) {
      console.error("Failed to save equipment preferences", error);
      // TODO: Show error toast
    } finally {
      setSavingEquipment(false);
      setEquipmentEdit(false);
    }
  };

  const saveLocation = async (locs: string[]) => {
    try {
      setSavingEquipment(true);
      const codes = Array.from(
        new Set(
          locs
            .map((l) => locationDisplayToCode(l))
            .filter((c): c is string => Boolean(c))
        )
      );
      const updatedOnboarding = await updateClientOnboarding(clientId, { location: codes });
      setWorkoutLocations(locs);
      setSelectedEquipmentIds(updatedOnboarding.equipmentIds || []);
      setClientProfileData((prev) => {
        if (!prev) return prev;
        return { ...prev, onboarding: updatedOnboarding };
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.clients.profile(clientId) });
    } catch (error) {
      console.error("Failed to save location", error);
      // TODO: Show error toast
    } finally {
      setSavingEquipment(false);
      setLocationEdit(false);
    }
  };

  // Checklist items state (mirrors client portal checklist tab)
  const [checklistItems, setChecklistItems] = useState<ChecklistItemToProfile[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(true);
  const [completingItemId, setCompletingItemId] = useState<string | null>(null);
  // Notes state (from API)
  const [notes, setNotes] = useState<NoteType[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteDialogMode, setNoteDialogMode] = useState<"create" | "edit">("create");
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteDialogText, setNoteDialogText] = useState("");
  const [noteDialogIsPrivate, setNoteDialogIsPrivate] = useState(false);
  const [noteDialogSaving, setNoteDialogSaving] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<NoteType | null>(null);
  const [noteDeleting, setNoteDeleting] = useState(false);

  // Fetch notes on mount
  useEffect(() => {
    if (!clientId) return;
    let active = true;
    (async () => {
      try {
        setNotesLoading(true);
        const fetchedNotes = await listNotes(clientId);
        if (!active) return;
        setNotes(fetchedNotes);
      } catch (error) {
        console.error("Failed to load notes", error);
        if (active) {
          setNotes([]);
        }
      } finally {
        if (active) {
          setNotesLoading(false);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [clientId]);

  const refreshNotes = async () => {
    const fetchedNotes = await listNotes(clientId);
    setNotes(fetchedNotes);
  };

  const openCreateNoteDialog = () => {
    setNoteDialogMode("create");
    setEditingNoteId(null);
    setNoteDialogText("");
    setNoteDialogIsPrivate(false);
    setNoteDialogOpen(true);
  };

  const openEditNoteDialog = (note: NoteType) => {
    setNoteDialogMode("edit");
    setEditingNoteId(note.id);
    setNoteDialogText(note.text);
    setNoteDialogIsPrivate(note.is_private_note_for_trainer_only);
    setNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (noteDialogSaving || !noteDialogText.trim()) return;
    const text = noteDialogText.trim();
    const isPrivate = noteDialogIsPrivate;
    const mode = noteDialogMode;
    const editingId = editingNoteId;

    setNoteDialogSaving(true);
    setNoteDialogOpen(false);
    setNoteDialogText("");
    setNoteDialogIsPrivate(false);
    setEditingNoteId(null);

    try {
      if (mode === "create") {
        await createNote({
          client_user_id: clientId,
          text,
          is_private_note_for_trainer_only: isPrivate,
        });
      } else if (editingId) {
        await updateNote(editingId, {
          text,
          is_private_note_for_trainer_only: isPrivate,
        });
      }
      await refreshNotes();
      toast.success(mode === "create" ? "Note added" : "Note updated");
    } catch (error) {
      console.error("Failed to save note", error);
      setNoteDialogMode(mode);
      setEditingNoteId(editingId);
      setNoteDialogText(text);
      setNoteDialogIsPrivate(isPrivate);
      setNoteDialogOpen(true);
      toast.error("Failed to save note");
    } finally {
      setNoteDialogSaving(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete || noteDeleting) return;
    setNoteDeleting(true);
    try {
      await deleteNote(noteToDelete.id);
      await refreshNotes();
      setNoteToDelete(null);
      toast.success("Note deleted");
    } catch (error) {
      console.error("Failed to delete note", error);
      toast.error("Failed to delete note");
    } finally {
      setNoteDeleting(false);
    }
  };


  // Program assignment state
  const [assignedClientProgram, setAssignedClientProgram] = useState<ClientProgram | null>(null);
  const [assignProgramDialogOpen, setAssignProgramDialogOpen] = useState(false);
  const [editAssignedProgramDialogOpen, setEditAssignedProgramDialogOpen] = useState(false);

  const getDifficultyColor = (level: string) => {
    const colorMap: Record<string, string> = {
      Beginner: "bg-green-100 text-green-800",
      Intermediate: "bg-yellow-100 text-yellow-800",
      Advanced: "bg-blue-100 text-blue-800",
      Expert: "bg-orange-100 text-orange-800",
      Pro: "bg-red-100 text-red-800",
    };
    return colorMap[level] || "bg-gray-100 text-gray-800";
  };

  const assignedProgram = assignedClientProgram?.program ?? null;

  // Load assigned program
  useEffect(() => {
    const loadAssignedProgram = async () => {
      if (!clientId) return;
      try {
        const clientPrograms = await getClientPrograms(clientId);
        if (clientPrograms.length > 0) {
          const latest = clientPrograms[0];
          setAssignedClientProgram(latest);
        } else {
          setAssignedClientProgram(null);
        }
      } catch (error: any) {
        console.error("Error loading assigned program:", error);
        setAssignedClientProgram(null);
      }
    };

    void loadAssignedProgram();
  }, [clientId]);

  const handleProgramAssigned = useCallback((clientProgram: ClientProgram) => {
    setAssignedClientProgram(clientProgram);
    setAssignProgramDialogOpen(false);
  }, []);

  const handleAssignedProgramSaved = useCallback((clientProgram: ClientProgram) => {
    setAssignedClientProgram(clientProgram);
    setEditAssignedProgramDialogOpen(false);
  }, []);

  // Workout load chart state for Overview tab (mirrors client profile)
  type LoadEntry = {
    timestamp: string;
    difficulty: number;
    points: number;
    intensity: number;
    energy: number;
    minutes: number;
  };

  const [workoutLoadEntries, setWorkoutLoadEntries] = useState<LoadEntry[]>([]);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  // Goal tracking (RHR, Weight, RPE)
  type GoalMetricKey = "resting_heart_rate" | "body_weight" | "rpe" | string;
  const [goalsSummary, setGoalsSummary] = useState<GoalsSummary | null>(null);
  const [goalsSummaryLoading, setGoalsSummaryLoading] = useState(false);
  const [goalsSummaryError, setGoalsSummaryError] = useState<string | null>(null);
  const [manageOptionalGoalsOpen, setManageOptionalGoalsOpen] = useState(false);

  const [goalLogOpen, setGoalLogOpen] = useState<GoalMetricKey | null>(null);
  const [goalDateRange, setGoalDateRange] = useState<DateRange | undefined>(undefined);
  const [goalLogLoading, setGoalLogLoading] = useState(false);
  const [goalLogError, setGoalLogError] = useState<string | null>(null);
  const [rhrEntries, setRhrEntries] = useState<RestingHrEntry[]>([]);
  const [weightEntries, setWeightEntries] = useState<BodyWeightEntry[]>([]);
  const [rpeEntries, setRpeEntries] = useState<RpeEntry[]>([]);
  const [optionalCatalog, setOptionalCatalog] = useState<GoalMetricCatalogItem[]>([]);
  const [enabledMetricCodes, setEnabledMetricCodes] = useState<string[]>([]);
  const [selectedOptionalCodes, setSelectedOptionalCodes] = useState<string[]>([]);
  const [optionalEntries, setOptionalEntries] = useState<OptionalMetricEntry[]>([]);
  const [optionalGoalsSaving, setOptionalGoalsSaving] = useState(false);

  const [goalAddOpen, setGoalAddOpen] = useState<GoalMetricKey | null>(null);
  const [goalAddValue, setGoalAddValue] = useState<string>("");
  const [goalAddDate, setGoalAddDate] = useState<string>(() => format(new Date(), "yyyy-MM-dd"));
  const [goalAddVariant, setGoalAddVariant] = useState<string>("3");
  const [goalAddSaving, setGoalAddSaving] = useState(false);

  // Filter workouts by date (past = history, future = scheduled)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const historyWorkouts = useMemo(() => {
    return workoutAssignments
      .filter((w) => {
        const workoutDate = parseYYYYMMDDToLocalDate(w.date);
        workoutDate.setHours(0, 0, 0, 0);
        return workoutDate < today;
      })
      .sort((a, b) => {
        // Sort history workouts by date descending (newest first)
        const dateA = parseYYYYMMDDToLocalDate(a.date).getTime();
        const dateB = parseYYYYMMDDToLocalDate(b.date).getTime();
        return dateB - dateA;
      });
  }, [workoutAssignments]);

  const scheduledWorkouts = useMemo(() => {
    return workoutAssignments
      .filter((w) => {
        const workoutDate = parseYYYYMMDDToLocalDate(w.date);
        workoutDate.setHours(0, 0, 0, 0);
        return workoutDate >= today;
      })
      .sort((a, b) => {
        // Sort scheduled workouts by date ascending (oldest first)
        const dateA = parseYYYYMMDDToLocalDate(a.date).getTime();
        const dateB = parseYYYYMMDDToLocalDate(b.date).getTime();
        return dateA - dateB;
      });
  }, [workoutAssignments]);

  // Group workouts by date for calendar view
  const workoutsByDate = useMemo(() => {
    const map = new Map<string, Array<{ id: string; name: string; status: "completed" | "scheduled" | "incomplete" }>>();
    
    workoutAssignments.forEach((workout) => {
      const dateKey = workout.date;
      if (!map.has(dateKey)) {
        map.set(dateKey, []);
      }
      const isCompleted = workout.completed_workout !== null;
      const workoutDate = parseYYYYMMDDToLocalDate(workout.date);
      workoutDate.setHours(0, 0, 0, 0);
      const isPast = workoutDate < today;
      
      map.get(dateKey)!.push({
        id: workout.id,
        name: workout.name,
        status: isCompleted ? "completed" : isPast ? "incomplete" : "scheduled",
      });
    });
    
    return map;
  }, [workoutAssignments]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("workoutLoad");
      if (!raw) {
        const now = new Date();
        const seeded: LoadEntry[] = Array.from({ length: 10 }, (_, idx) => {
          const d = new Date(now);
          d.setDate(now.getDate() - (9 - idx));
          const difficulty = Math.max(1, Math.min(10, Math.floor(Math.random() * 10) + 1));
          const points = 1000 + Math.floor(Math.random() * 1500); // 1000–2499
          const intensity = Math.max(
            1,
            Math.min(10, Math.floor(Math.random() * 10) + 1)
          );
          const energy = Math.max(
            1,
            Math.min(10, Math.floor(Math.random() * 10) + 1)
          );
          const minutes = 20 + Math.floor(Math.random() * 40); // 20–59
          return { timestamp: d.toISOString(), difficulty, points, intensity, energy, minutes };
        });
        localStorage.setItem("workoutLoad", JSON.stringify(seeded));
        setWorkoutLoadEntries(seeded);
      } else {
        const parsed: LoadEntry[] = JSON.parse(raw);
        setWorkoutLoadEntries(parsed);
      }
    } catch {
      setWorkoutLoadEntries([]);
    }
  }, []);

  const filteredWorkoutLoadEntries = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return workoutLoadEntries;
    const start = new Date(dateRange.from);
    start.setHours(0, 0, 0, 0);
    const end = new Date(dateRange.to);
    end.setHours(23, 59, 59, 999);
    return workoutLoadEntries.filter((e) => {
      const d = new Date(e.timestamp);
      return d >= start && d <= end;
    });
  }, [workoutLoadEntries, dateRange]);

  const goalRangeISO = useMemo(
    () => completeDateRangeToFilterISO(goalDateRange),
    [goalDateRange]
  );

  const openGoalLog = useCallback((metric: GoalMetricKey) => {
    setGoalDateRange(undefined);
    setGoalLogOpen(metric);
  }, []);

  const loadGoalsSummary = useCallback(async () => {
    if (!clientId) return;
    try {
      setGoalsSummaryLoading(true);
      setGoalsSummaryError(null);
      const s = await adminGetGoalsSummary(clientId);
      setGoalsSummary(s);
    } catch (e: any) {
      setGoalsSummary(null);
      setGoalsSummaryError(e?.message || "Failed to load goal tracking");
    } finally {
      setGoalsSummaryLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    void loadGoalsSummary();
  }, [loadGoalsSummary]);

  const refetchOptionalGoalMeta = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.goals.availableMetrics });
    await queryClient.invalidateQueries({ queryKey: queryKeys.goals.adminEnabledMetrics(clientId) });
  }, [clientId, queryClient]);

  useEffect(() => {
    const catalog = goalCatalogQuery.data;
    const enabled = enabledMetricsQuery.data;
    if (!catalog || !enabled) return;
    setOptionalCatalog(catalog);
    setEnabledMetricCodes(enabled.metric_codes || []);
    const optionalSet = new Set(catalog.map((c) => c.code));
    const selected = (enabled.metric_codes || []).filter((c) => optionalSet.has(c));
    setSelectedOptionalCodes(selected);
  }, [goalCatalogQuery.data, enabledMetricsQuery.data]);

  const loadGoalLog = useCallback(
    async (metric: GoalMetricKey) => {
      if (!clientId) return;
      try {
        setGoalLogLoading(true);
        setGoalLogError(null);
        const { from, to } = goalRangeISO;
        if (metric === "resting_heart_rate") {
          setRhrEntries(await adminListRestingHr(clientId, from, to));
        } else if (metric === "body_weight") {
          setWeightEntries(await adminListBodyWeight(clientId, from, to));
        } else if (metric === "rpe") {
          setRpeEntries(await adminListRpe(clientId, from, to));
        } else {
          setOptionalEntries(await adminListOptionalMetricEntries(clientId, metric, from, to));
        }
      } catch (e: any) {
        setGoalLogError(e?.message || "Failed to load log");
      } finally {
        setGoalLogLoading(false);
      }
    },
    [clientId, goalRangeISO]
  );

  useEffect(() => {
    if (!goalLogOpen) return;
    void loadGoalLog(goalLogOpen);
  }, [goalLogOpen, goalRangeISO.from, goalRangeISO.to, loadGoalLog]);

  const openGoalAdd = useCallback((metric: GoalMetricKey) => {
    setGoalAddOpen(metric);
    setGoalAddValue("");
    setGoalAddDate(format(new Date(), "yyyy-MM-dd"));
    setGoalAddVariant("3");
  }, []);

  const saveGoalAdd = useCallback(async () => {
    if (!clientId || !goalAddOpen) return;
    try {
      setGoalAddSaving(true);
      const v = Number(goalAddValue);
      if (!Number.isFinite(v)) throw new Error("Please enter a number");
      if (goalAddOpen === "resting_heart_rate") {
        await adminCreateRestingHr(clientId, Math.round(v), goalAddDate);
      } else if (goalAddOpen === "body_weight") {
        await adminCreateBodyWeight(clientId, v, goalAddDate);
      } else if (goalAddOpen === "rpe") {
        // RPE is captured via workout completion
      } else {
        const item = optionalCatalog.find((c) => c.code === goalAddOpen);
        const variant = item?.supports_variant ? Number(goalAddVariant) : undefined;
        await adminCreateOptionalMetricEntry(clientId, goalAddOpen, v, goalAddDate, variant);
      }
      setGoalAddOpen(null);
      await loadGoalsSummary();
      await refetchOptionalGoalMeta();
      if (goalLogOpen === goalAddOpen) {
        await loadGoalLog(goalAddOpen);
      }
      toast.success("Saved");
    } catch (e: any) {
      toast.error("Failed to save", { description: e?.message || String(e) });
    } finally {
      setGoalAddSaving(false);
    }
  }, [clientId, goalAddDate, goalAddOpen, goalAddValue, goalAddVariant, goalLogOpen, loadGoalLog, loadGoalsSummary, refetchOptionalGoalMeta, optionalCatalog]);

  const baseLoadEntries = useMemo(
    () =>
      dateRange?.from && dateRange?.to
        ? filteredWorkoutLoadEntries
        : workoutLoadEntries.slice(-14),
    [workoutLoadEntries, filteredWorkoutLoadEntries, dateRange?.from, dateRange?.to]
  );

  const buildSeries = (metric: keyof LoadEntry) =>
    baseLoadEntries.map((e) => {
      const d = new Date(e.timestamp);
      const label = `${d.getMonth() + 1}/${d.getDate()}`;
      return { date: label, value: e[metric] as number };
    });

  const pointsSeriesForProfile = useMemo(
    () => buildSeries("points"),
    [baseLoadEntries]
  );
  const intensitySeriesForProfile = useMemo(
    () => buildSeries("intensity"),
    [baseLoadEntries]
  );
  const energySeriesForProfile = useMemo(
    () => buildSeries("energy"),
    [baseLoadEntries]
  );
  const minutesSeriesForProfile = useMemo(
    () => buildSeries("minutes"),
    [baseLoadEntries]
  );

  const averageOfSeries = (series: Array<{ date: string; value: number }>) =>
    series.length === 0
      ? 0
      : series.reduce((sum, p) => sum + (p.value || 0), 0) / series.length;
  const avgPointsForProfile = useMemo(
    () => averageOfSeries(pointsSeriesForProfile),
    [pointsSeriesForProfile]
  );
  const avgIntensityForProfile = useMemo(
    () => averageOfSeries(intensitySeriesForProfile),
    [intensitySeriesForProfile]
  );
  const avgEnergyForProfile = useMemo(
    () => averageOfSeries(energySeriesForProfile),
    [energySeriesForProfile]
  );
  const avgMinutesForProfile = useMemo(
    () => averageOfSeries(minutesSeriesForProfile),
    [minutesSeriesForProfile]
  );
  const totalWorkouts = historyWorkouts.filter((w) => w.completed_workout !== null).length;
  const totalPoints = historyWorkouts.reduce((sum, w) => sum + (w.completed_workout?.points || 0), 0);
  const totalMinutes = historyWorkouts.reduce((sum, w) => sum + (w.completed_workout?.minutes || 0), 0);

  const metricLabels: Record<string, string> = {
    "body-weight": "Body Weight",
    "body-fat": "Body Fat %",
    "bmi": "BMI",
    "visceral-fat": "Visceral Fat",
    "jeans-size": "Jeans Size",
    "tshirt-size": "T-shirt Size",
    "energy-level": "Energy Level",
    "strength": "Strength",
  };

  const getLastMetricValue = (metricId: string): string | undefined => {
    // Find latest update containing this metric
    const sorted = [...progressUpdates].sort(
      (a, b) => parseYYYYMMDDToLocalDate(b.date).getTime() - parseYYYYMMDDToLocalDate(a.date).getTime()
    );
    for (const rec of sorted) {
      if (rec.metrics[metricId]) return rec.metrics[metricId];
    }
    return undefined;
  };

  const getEnergyBadgeClasses = (level: string) => {
    switch (level) {
      case "supercharged":
        return "bg-emerald-100 text-emerald-700";
      case "ready":
        return "bg-blue-100 text-blue-700";
      case "tired":
        return "bg-orange-100 text-orange-700";
      case "low":
        return "bg-red-100 text-red-700";
      case "injured":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getEnergyLabel = (level: string) => {
    const map: Record<string, string> = {
      supercharged: "Supercharged",
      ready: "Ready",
      tired: "Tired",
      low: "Low",
      injured: "Injured",
    };
    return map[level] || level;
  };

  // Show loading state
  if (clientProfileLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Loading client profile...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (clientProfileError || !clientProfileData) {
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          <span>Back to clients</span>
        </button>
        <div className="text-center py-12">
          <p className="text-red-600">
            {clientProfileError || "Failed to load client profile"}
          </p>
        </div>
      </div>
    );
  }

  const profile = clientProfileData.profile;
  const onboardingData = clientProfileData.onboarding;
  const workoutGeneratorPhaseId = searchParams.get("phaseId");

  if (activeTab === "workout-generator" && workoutGeneratorPhaseId) {
    return (
      <div className="space-y-6">
        <div className="space-y-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            <span>Back to clients</span>
          </button>
        </div>
        <WorkoutGeneratorPage
          clientId={clientId}
          phaseId={workoutGeneratorPhaseId}
          onBack={() => {
            const newParams = new URLSearchParams(searchParams);
            newParams.set("clientTab", "program");
            newParams.delete("phaseId");
            setSearchParams(newParams, { replace: true });
            setActiveTab("program");
          }}
          onNavigateToWorkouts={() => {
            const newParams = new URLSearchParams(searchParams);
            newParams.set("clientTab", "workouts");
            newParams.set("workoutView", "scheduled");
            newParams.delete("phaseId");
            setSearchParams(newParams, { replace: true });
            setWorkoutView("scheduled");
            setActiveTab("workouts");
            void refreshWorkoutAssignments();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          <span>Back to clients</span>
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Avatar className="w-16 h-16">
              <AvatarFallback className="bg-gradient-to-br from-emerald-400 to-emerald-500 text-white text-xl font-bold">
                {getInitials(profile.first_name || "", profile.last_name || "")}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                {profile.first_name || ""} {profile.last_name || ""}
              </h1>
              <p className="text-gray-600">{profile.email || ""}</p>
            </div>
          </div>
          
          <div className="flex space-x-3">
            <Button
              variant="outline"
              className="border-gray-300 text-gray-800 hover:bg-gray-100 hover:text-gray-900"
              onClick={() => setActiveTab("chat")}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Message
            </Button>
            <Button 
              className="bg-primary text-black hover:bg-primary/90"
              onClick={() => {
                setAssignWorkoutDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Assign Workout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-10">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="checklist">Checklist</TabsTrigger>
          <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
          <TabsTrigger value="program">Program</TabsTrigger>
          <TabsTrigger value="workouts">Workouts</TabsTrigger>
          <TabsTrigger value="equipment">Equipment</TabsTrigger>
          <TabsTrigger value="progress">Progress</TabsTrigger>
          <TabsTrigger value="files">Files</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="chat">Chat</TabsTrigger>
        </TabsList>
        <TabsContent value="progress">
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Goals</h3>

            <Collapsible open={manageOptionalGoalsOpen} onOpenChange={setManageOptionalGoalsOpen}>
              <Card className="border-0 shadow-lg">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-gray-50/50 transition-colors rounded-t-lg">
                    <div className="flex items-center justify-between">
                      <CardTitle>Manage optional goals</CardTitle>
                      {manageOptionalGoalsOpen ? <ChevronDown className="h-4 w-4 text-gray-600" /> : <ChevronRight className="h-4 w-4 text-gray-600" />}
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    <p className="text-sm text-gray-600">
                      Select which optional goals this client should track. They’ll show up in the client portal.
                    </p>
                    {optionalCatalog.length === 0 ? (
                      <div className="text-sm text-gray-500">No optional goals available.</div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {optionalCatalog.map((m) => {
                          const checked = selectedOptionalCodes.includes(m.code);
                          return (
                            <label key={m.code} className="flex items-center gap-3 rounded-lg border border-gray-200 p-3">
                              <Checkbox
                                className="border-gray-500 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600 data-[state=checked]:[&_svg]:text-white"
                                checked={checked}
                                onCheckedChange={(v) => {
                                  const next = Boolean(v);
                                  setSelectedOptionalCodes((prev) => {
                                    const s = new Set(prev);
                                    if (next) s.add(m.code);
                                    else s.delete(m.code);
                                    return Array.from(s);
                                  });
                                }}
                              />
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900">{m.label}</div>
                                <div className="text-xs text-gray-500">
                                  Unit: {m.unit}
                                  {m.supports_variant ? " • Supports 3/5/8 selector" : ""}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => void refetchOptionalGoalMeta()}
                        disabled={optionalGoalsSaving}
                      >
                        Reset
                      </Button>
                      <Button
                        onClick={async () => {
                          if (!clientId) return;
                          setManageOptionalGoalsOpen(false);
                          try {
                            setOptionalGoalsSaving(true);
                            const enabled = await adminSetEnabledGoalMetrics(clientId, selectedOptionalCodes);
                            const optionalSet = new Set(optionalCatalog.map((c) => c.code));
                            setEnabledMetricCodes([
                              "resting_heart_rate",
                              "body_weight",
                              "rpe",
                              ...enabled.filter((c) => optionalSet.has(c)),
                            ]);
                            toast.success("Updated optional goals");
                            await loadGoalsSummary();
                            await refetchOptionalGoalMeta();
                          } catch (e: any) {
                            toast.error("Failed to update optional goals", { description: e?.message || String(e) });
                          } finally {
                            setOptionalGoalsSaving(false);
                          }
                        }}
                        disabled={optionalGoalsSaving}
                      >
                        {optionalGoalsSaving ? "Saving..." : "Save"}
                      </Button>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Goal Tracking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                    {goalsSummaryLoading ? (
                      <div className="space-y-3">
                        <div className="h-6 w-56 rounded bg-gray-100 animate-pulse" />
                        <div className="h-24 w-full rounded bg-gray-100 animate-pulse" />
                        <div className="h-24 w-full rounded bg-gray-100 animate-pulse" />
                        <div className="h-24 w-full rounded bg-gray-100 animate-pulse" />
                      </div>
                    ) : goalsSummaryError ? (
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">Couldn’t load goal tracking.</p>
                        <p className="text-xs text-gray-500 break-words">{goalsSummaryError}</p>
                        <Button variant="outline" onClick={() => void loadGoalsSummary()}>
                          Retry
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4">
                        {([
                          { key: "resting_heart_rate" as const, title: "Resting Heart Rate", unit: "bpm", allowAdd: true },
                          { key: "body_weight" as const, title: "Body Weight", unit: "lbs", allowAdd: true },
                          { key: "rpe" as const, title: "RPE", unit: "/ 10", allowAdd: false },
                          ...optionalCatalog
                            .filter((c) => enabledMetricCodes.includes(c.code))
                            .map((c) => ({ key: c.code as GoalMetricKey, title: c.label, unit: c.unit, allowAdd: true })),
                        ] as const).map((m: any) => {
                          const s =
                            m.key === "resting_heart_rate" || m.key === "body_weight" || m.key === "rpe"
                              ? (goalsSummary as any)?.[m.key]
                              : goalsSummary?.optional?.[m.key];
                          const start = s?.start ?? null;
                          const current = s?.current ?? null;
                          const ds = s?.delta_since_start ?? null;
                          const dl = s?.delta_since_last ?? null;
                          const fmt = (n: number | null) => (n === null ? "—" : `${n}`);
                          return (
                            <div key={m.key} className="rounded-xl border border-gray-200 p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="font-semibold text-gray-900">{m.title}</div>
                                  <div className="text-xs text-gray-500">
                                    {m.key === "rpe" ? "Captured after each completed workout." : "Trainer or client can add entries."}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {m.allowAdd ? (
                                    <Button size="sm" variant="outline" onClick={() => openGoalAdd(m.key)}>
                                      Add
                                    </Button>
                                  ) : null}
                                  <Button size="sm" variant="ghost" onClick={() => openGoalLog(m.key)}>
                                    View log
                                  </Button>
                                </div>
                              </div>
                              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="rounded-lg bg-gray-50 p-3">
                                  <div className="text-[11px] text-gray-500">Start</div>
                                  <div className="font-semibold text-gray-900">
                                    {start === null ? "—" : `${start} ${m.unit}`.trim()}
                                  </div>
                                </div>
                                <div className="rounded-lg bg-gray-50 p-3">
                                  <div className="text-[11px] text-gray-500">Current</div>
                                  <div className="font-semibold text-gray-900">
                                    {current === null ? "—" : `${current} ${m.unit}`.trim()}
                                  </div>
                                </div>
                                <div className="rounded-lg bg-gray-50 p-3">
                                  <div className="text-[11px] text-gray-500">Since start</div>
                                  <div className="font-semibold text-gray-900">{fmt(ds)}</div>
                                </div>
                                <div className="rounded-lg bg-gray-50 p-3">
                                  <div className="text-[11px] text-gray-500">Since last</div>
                                  <div className="font-semibold text-gray-900">{fmt(dl)}</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Dialog
                  open={goalLogOpen !== null}
                  onOpenChange={(open) => {
                    if (!open) {
                      setGoalLogOpen(null);
                      setGoalDateRange(undefined);
                    }
                  }}
                >
                  <DialogScrollContent className="max-w-3xl">
                    <DialogHeader className="shrink-0">
                      <DialogTitle>
                        {goalLogOpen === "resting_heart_rate"
                          ? "Resting Heart Rate Log"
                          : goalLogOpen === "body_weight"
                            ? "Body Weight Log"
                            : goalLogOpen === "rpe"
                              ? "RPE Log"
                              : optionalCatalog.find((c) => c.code === goalLogOpen)?.label || "Goal Log"}
                      </DialogTitle>
                      <DialogDescription>Filter by date range, view trend, and review entries.</DialogDescription>
                    </DialogHeader>

                    <div className="shrink-0 flex items-center justify-between gap-2">
                      <Popover modal={false}>
                        <PopoverTrigger asChild>
                          <Button variant="outline">{formatGoalDateRangeLabel(goalDateRange)}</Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <DateRangeCalendar mode="range" numberOfMonths={2} selected={goalDateRange} onSelect={setGoalDateRange} />
                        </PopoverContent>
                      </Popover>
                      {goalDateRange?.from ? (
                        <Button variant="ghost" onClick={() => setGoalDateRange(undefined)}>
                          Reset
                        </Button>
                      ) : null}
                    </div>

                    <DialogBody className="space-y-4">
                    {goalLogError ? <div className="text-sm text-red-600">{goalLogError}</div> : null}

                    <div className="space-y-4">
                      <Card className="border border-gray-200">
                        <CardContent className="p-3">
                          {goalLogLoading ? (
                            <div className="h-44 w-full rounded bg-gray-100 animate-pulse" />
                          ) : (
                            (() => {
                              const data =
                                goalLogOpen === "resting_heart_rate"
                                  ? rhrEntries.map((e) => ({ date: e.recorded_on || "", value: e.bpm }))
                                  : goalLogOpen === "body_weight"
                                    ? weightEntries.map((e) => ({ date: e.recorded_on || "", value: e.weight_lbs }))
                                    : goalLogOpen === "rpe"
                                      ? rpeEntries.map((e) => ({ date: e.date || "", value: e.rpe }))
                                      : optionalEntries.map((e) => ({ date: e.recorded_on || "", value: e.value }));
                              if (data.length === 0) {
                                return <div className="text-sm text-gray-500 py-6 text-center">No entries yet.</div>;
                              }
                              return (
                                <ChartContainer config={{ metric: { label: "Metric", color: "hsl(200 80% 50%)" } }} className="h-44 w-full">
                                  <LineChart data={data}>
                                    <CartesianGrid strokeDasharray="4 4" stroke="#E5E7EB" />
                                    <XAxis
                                      dataKey="date"
                                      tickLine={false}
                                      axisLine={false}
                                      tickMargin={8}
                                      stroke="#6B7280"
                                      tickFormatter={(v) => formatISODateLabelShort(String(v))}
                                    />
                                    <YAxis domain={goalLogOpen === "rpe" ? ([1, 10] as any) : (["auto", "auto"] as any)} tickLine={false} axisLine={false} tickMargin={8} stroke="#6B7280" />
                                    <ChartTooltip content={<ChartTooltipContent labelKey="metric" />} />
                                    <Line type="monotone" dataKey="value" stroke="var(--color-metric)" strokeWidth={3} dot={{ r: 3 }} />
                                  </LineChart>
                                </ChartContainer>
                              );
                            })()
                          )}
                        </CardContent>
                      </Card>

                      <Card className="border border-gray-200">
                        <CardContent className="p-3">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Value</TableHead>
                                <TableHead>Entered by</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(goalLogOpen === "resting_heart_rate"
                                ? [...rhrEntries].reverse().map((e) => ({
                                    key: e.id,
                                    date: e.recorded_on,
                                    value: `${e.bpm} bpm`,
                                    by: e.created_by?.name ?? "—",
                                  }))
                                : goalLogOpen === "body_weight"
                                  ? [...weightEntries].reverse().map((e) => ({
                                      key: e.id,
                                      date: e.recorded_on,
                                      value: `${e.weight_lbs} lbs`,
                                      by: e.created_by?.name ?? "—",
                                    }))
                                  : goalLogOpen === "rpe"
                                    ? [...rpeEntries].reverse().map((e) => ({
                                        key: e.workout_assigned_id,
                                        date: e.date,
                                        value: `${e.rpe} / 10`,
                                        by: "App",
                                      }))
                                    : [...optionalEntries].reverse().map((e) => ({
                                        key: e.id,
                                        date: e.recorded_on,
                                        value: `${e.value}${e.variant ? ` (x${e.variant})` : ""}`,
                                        by: e.created_by?.name ?? "—",
                                      }))
                              ).map((row) => (
                                <TableRow key={row.key}>
                                  <TableCell>{row.date || "—"}</TableCell>
                                  <TableCell>{row.value}</TableCell>
                                  <TableCell className="text-gray-500">{row.by}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    </div>
                    </DialogBody>
                  </DialogScrollContent>
                </Dialog>

                <Dialog open={goalAddOpen !== null} onOpenChange={(open) => !open && setGoalAddOpen(null)}>
                  <DialogScrollContent className="max-w-md border border-gray-200 bg-white text-gray-900">
                    <DialogHeader className="shrink-0">
                      <DialogTitle className="text-gray-900">
                        {goalAddOpen === "resting_heart_rate"
                          ? "Add Resting Heart Rate"
                          : goalAddOpen === "body_weight"
                            ? "Add Body Weight"
                            : goalAddOpen === "rpe"
                              ? "Add"
                              : optionalCatalog.find((c) => c.code === goalAddOpen)?.label || "Add"}
                      </DialogTitle>
                      <DialogDescription className="text-gray-600">Creates (or updates) the entry for that date.</DialogDescription>
                    </DialogHeader>
                    <DialogBody className="space-y-3">
                      <div>
                        <Label className="text-gray-900">Value</Label>
                        <Input
                          type="number"
                          inputMode="decimal"
                          value={goalAddValue}
                          onChange={(e) => setGoalAddValue(e.target.value)}
                          className="border-gray-300 bg-white text-gray-900 placeholder:text-gray-500"
                          placeholder={
                            goalAddOpen === "resting_heart_rate"
                              ? "e.g. 55"
                              : goalAddOpen === "body_weight"
                                ? "e.g. 180.5"
                                : "e.g. 10"
                          }
                        />
                      </div>
                      {goalAddOpen &&
                      goalAddOpen !== "resting_heart_rate" &&
                      goalAddOpen !== "body_weight" &&
                      goalAddOpen !== "rpe" &&
                      optionalCatalog.find((c) => c.code === goalAddOpen)?.supports_variant ? (
                        <div>
                          <Label className="text-gray-900">Variant</Label>
                          <select
                            value={goalAddVariant}
                            onChange={(e) => setGoalAddVariant(e.target.value)}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900"
                          >
                            <option value="3">3RM</option>
                            <option value="5">5RM</option>
                            <option value="8">8RM</option>
                          </select>
                        </div>
                      ) : null}
                      <div>
                        <Label className="text-gray-900">Date</Label>
                        <Input
                          type="date"
                          value={goalAddDate}
                          onChange={(e) => setGoalAddDate(e.target.value)}
                          className="border-gray-300 bg-white text-gray-900"
                        />
                      </div>
                    </DialogBody>
                    <DialogFooter className="shrink-0">
                      <Button variant="ghost" onClick={() => setGoalAddOpen(null)} className="text-gray-700 hover:bg-gray-100 hover:text-gray-900">
                        Cancel
                      </Button>
                      <Button onClick={() => void saveGoalAdd()} disabled={goalAddSaving}>
                        {goalAddSaving ? "Saving..." : "Save"}
                      </Button>
                    </DialogFooter>
                  </DialogScrollContent>
                </Dialog>
          </div>
        </TabsContent>

        <TabsContent value="overview">
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <p className="text-3xl font-bold text-gray-800">{totalWorkouts}</p>
                <p className="text-gray-600 text-sm">Total Workouts</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-500 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <p className="text-3xl font-bold text-gray-800">{achievements.length}</p>
                <p className="text-gray-600 text-sm">Trophies Earned</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <p className="text-3xl font-bold text-gray-800">{totalPoints}</p>
                <p className="text-gray-600 text-sm">Points Earned</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-500 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <p className="text-3xl font-bold text-gray-800">
                  {formatDuration(totalMinutes)}
                </p>
                <p className="text-gray-600 text-sm">Total Time</p>
              </CardContent>
            </Card>
          </div>

          {/* Load & trends charts */}
          <Card className="border border-gray-200 bg-white">
            <CardHeader className="pb-2">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="flex items-center space-x-2 text-gray-900">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <span>Training trends</span>
                </CardTitle>
                <div className="flex items-center justify-end gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="border-gray-300 text-gray-800 hover:bg-gray-100"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {dateRange?.from && dateRange?.to
                          ? `${format(dateRange.from, "MMM d, yyyy")} - ${format(
                              dateRange.to,
                              "MMM d, yyyy"
                            )}`
                          : "All time"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 bg-white border border-gray-200 text-gray-900"
                      align="end"
                    >
                      <DateRangeCalendar
                        mode="range"
                        numberOfMonths={2}
                        selected={dateRange}
                        onSelect={setDateRange}
                      />
                    </PopoverContent>
                  </Popover>
                  {dateRange?.from && dateRange?.to && (
                    <Button
                      variant="ghost"
                      className="text-gray-500 hover:text-gray-800"
                      onClick={() => setDateRange(undefined)}
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reset
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {baseLoadEntries.length === 0 ? (
                <div className="text-sm text-gray-500 py-6 text-center">
                  No data yet. Complete a workout and rate its difficulty to see
                  trends.
                </div>
              ) : (
                <div className="space-y-6">
                  {/* 1. Load points */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                      Load points
                    </h4>
                    <ChartContainer
                      config={{ load: { label: "Points", color: "hsl(200 80% 50%)" } }}
                      className="h-48 w-full"
                    >
                      <ComposedChart
                        data={pointsSeriesForProfile}
                        margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="4 4" stroke="#E5E7EB" />
                        <XAxis
                          dataKey="date"
                          tickLine={false}
                          axisLine={false}
                          tickMargin={8}
                          stroke="#9CA3AF"
                        />
                        <YAxis
                          allowDecimals={false}
                          tickLine={false}
                          axisLine={false}
                          tickMargin={8}
                          stroke="#9CA3AF"
                        />
                        <ChartTooltip content={<ChartTooltipContent labelKey="load" />} />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="var(--color-load)"
                          strokeWidth={3}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                        {avgPointsForProfile > 0 && (
                          <ReferenceLine
                            y={avgPointsForProfile}
                            stroke="#9CA3AF"
                            strokeDasharray="3 3"
                            strokeWidth={1}
                            label={{
                              value: "Avg",
                              position: "right",
                              fill: "#6B7280",
                              fontSize: 12,
                            }}
                          />
                        )}
                      </ComposedChart>
                    </ChartContainer>
                  </div>

                  {/* 2. Intensity */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                      Intensity (1–10)
                    </h4>
                    <ChartContainer
                      config={{
                        load: { label: "Intensity", color: "hsl(30 90% 55%)" },
                      }}
                      className="h-48 w-full"
                    >
                      <ComposedChart
                        data={intensitySeriesForProfile}
                        margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                      >
                        <CartesianGrid strokeDasharray="4 4" stroke="#E5E7EB" />
                        <XAxis
                          dataKey="date"
                          tickLine={false}
                          axisLine={false}
                          tickMargin={8}
                          stroke="#9CA3AF"
                        />
                        <YAxis
                          domain={[1, 10]}
                          allowDecimals={false}
                          tickCount={10}
                          tickLine={false}
                          axisLine={false}
                          tickMargin={8}
                          stroke="#9CA3AF"
                        />
                        <ChartTooltip content={<ChartTooltipContent labelKey="load" />} />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="var(--color-intensity)"
                          strokeWidth={3}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                        {avgIntensityForProfile > 0 && (
                          <ReferenceLine
                            y={avgIntensityForProfile}
                            stroke="#9CA3AF"
                            strokeDasharray="3 3"
                            strokeWidth={1}
                            label={{
                              value: "Avg",
                              position: "right",
                              fill: "#6B7280",
                              fontSize: 12,
                            }}
                          />
                        )}
                      </ComposedChart>
                    </ChartContainer>
                  </div>

                  {/* 3. Energy */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                      Energy (1–10)
                    </h4>
                    <ChartContainer
                      config={{
                        load: { label: "Energy", color: "hsl(140 80% 50%)" },
                      }}
                      className="h-48 w-full"
                    >
                        <ComposedChart
                          data={energySeriesForProfile}
                          margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                        >
                          <CartesianGrid strokeDasharray="4 4" stroke="#E5E7EB" />
                          <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            stroke="#9CA3AF"
                          />
                          <YAxis
                            domain={[1, 10]}
                            allowDecimals={false}
                            tickCount={10}
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            stroke="#9CA3AF"
                          />
                          <ChartTooltip content={<ChartTooltipContent labelKey="load" />} />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke="var(--color-energy)"
                            strokeWidth={3}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                          {avgEnergyForProfile > 0 && (
                            <ReferenceLine
                              y={avgEnergyForProfile}
                              stroke="#9CA3AF"
                              strokeDasharray="3 3"
                              strokeWidth={1}
                              label={{
                                value: "Avg",
                                position: "right",
                                fill: "#6B7280",
                                fontSize: 12,
                              }}
                            />
                          )}
                        </ComposedChart>
                    </ChartContainer>
                  </div>

                  {/* 4. Time logged */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                      Time logged (minutes)
                    </h4>
                    <ChartContainer
                      config={{
                        load: { label: "Minutes", color: "hsl(260 80% 55%)" },
                      }}
                      className="h-48 w-full"
                    >
                        <ComposedChart
                          data={minutesSeriesForProfile}
                          margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                        >
                          <CartesianGrid strokeDasharray="4 4" stroke="#E5E7EB" />
                          <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            stroke="#9CA3AF"
                          />
                          <YAxis
                            allowDecimals={false}
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            stroke="#9CA3AF"
                          />
                          <ChartTooltip content={<ChartTooltipContent labelKey="load" />} />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke="var(--color-minutes)"
                            strokeWidth={3}
                            dot={{ r: 3 }}
                            activeDot={{ r: 5 }}
                          />
                          {avgMinutesForProfile > 0 && (
                            <ReferenceLine
                              y={avgMinutesForProfile}
                              stroke="#9CA3AF"
                              strokeDasharray="3 3"
                              strokeWidth={1}
                              label={{
                                value: "Avg",
                                position: "right",
                                fill: "#6B7280",
                                fontSize: 12,
                              }}
                            />
                          )}
                        </ComposedChart>
                      </ChartContainer>
                    </div>
                  </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="onboarding" className="space-y-6">
          {/* Edit controls */}
          <div className="flex items-center justify-end">
            {!onboardingEdit ? (
              <Button
                variant="outline"
                className="border-gray-300 text-gray-900 bg-white hover:bg-gray-100"
                onClick={() => setOnboardingEdit(true)}
              >
                Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  className="bg-primary text-black hover:bg-primary/90"
                  disabled={savingOnboarding}
                  onClick={async () => {
                    try {
                      await saveOnboarding();
                      setOnboardingEdit(false);
                    } catch {
                      // Keep edit mode open on error so the admin can retry.
                    }
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="outline"
                  className="border-primary/30 text-primary hover:bg-primary hover:text-black"
                  disabled={savingOnboarding}
                  onClick={() => {
                    const s = onboardingSummary;
                    setForm({
                      gender: s.gender || "",
                      dob: s.dob || "",
                      heightUnit: s.height.unit || "imperial",
                      heightFeet: String(s.height.feet ?? ""),
                      heightInches: String(s.height.inches ?? ""),
                      heightCm: String(s.height.cm ?? ""),
                      weightUnit: s.weight.unit || "lb",
                      weightValue: String(s.weight.value ?? ""),
                      motivations: [...s.motivations],
                      activityLevel: s.activityLevel || "",
                      workoutFrequency: s.workoutFrequency || "",
                      workoutDuration: s.workoutDuration || "",
                      exerciseTypes: [...s.exerciseTypes],
                      sportsEnjoyed: s.sportsEnjoyed || "",
                      variety: s.variety || "",
                      timeline: s.timeline || "",
                      services: [...s.additionalServices],
                      trainingForEvent: s.trainingForEvent || "",
                      eventDetails: s.eventDetails || "",
                      eventDate: s.eventDate || "",
                    });
                    setOnboardingEdit(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {/* Basics */}
          <Card className="border border-gray-200 bg-white text-gray-900">
              <CardHeader>
              <CardTitle className="flex items-center">
                <User className="w-5 h-5 mr-2 text-primary" />
                Basics
              </CardTitle>
              </CardHeader>
            <CardContent>
              {!onboardingEdit ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="text-xs text-gray-600">Gender</div>
                    <div className="font-medium text-gray-900 capitalize">
                      {onboardingSummary.gender ? onboardingSummary.gender.split("-").join(" ") : "Not specified"}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="text-xs text-gray-600">Date of Birth</div>
                    <div className="font-medium text-gray-900">
                      {onboardingSummary.dob
                        ? parseYYYYMMDDToLocalDate(onboardingSummary.dob).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })
                        : "Not specified"}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="text-xs text-gray-600">Height</div>
                    <div className="font-medium text-gray-900">
                      {onboardingSummary.height.unit === "imperial"
                        ? onboardingSummary.height.feet !== null || onboardingSummary.height.inches !== null
                          ? `${onboardingSummary.height.feet ?? 0} ft ${onboardingSummary.height.inches ?? 0} in`
                          : "Not specified"
                        : onboardingSummary.height.unit === "metric"
                        ? onboardingSummary.height.cm !== null
                          ? `${onboardingSummary.height.cm} cm`
                          : "Not specified"
                        : "Not specified"}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="text-xs text-gray-600">Weight</div>
                    <div className="font-medium text-gray-900">
                      {onboardingSummary.weight.value !== null && onboardingSummary.weight.unit
                        ? `${onboardingSummary.weight.value} ${onboardingSummary.weight.unit === "kg" ? "kg" : "lbs"}`
                        : "Not specified"}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-800">Gender</Label>
                      <Select
                        value={form.gender}
                        onValueChange={(v) => setForm((f) => ({ ...f, gender: v }))}
                      >
                        <SelectTrigger className="bg-white border border-gray-300 text-gray-900">
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent className="bg-white text-gray-900 border border-gray-200">
                          {genderOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} className="text-gray-900">
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                        </div>
                    <div className="space-y-2">
                      <Label className="text-gray-800">Date of Birth</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal bg-white border-gray-300 text-gray-900",
                              !form.dob && "text-gray-500"
                            )}
                          >
                            {(() => {
                              const iso =
                                form.dob && /^\d{4}-\d{2}-\d{2}$/.test(form.dob)
                                  ? parseYYYYMMDDToLocalDate(form.dob)
                                  : null;
                              const us = form.dob
                                ? parse(form.dob, "MM/dd/yyyy", new Date())
                                : null;
                              const date =
                                iso && isValidDate(iso)
                                  ? iso
                                  : us && isValidDate(us)
                                  ? us
                                  : null;
                              return date ? format(date, "MM/dd/yyyy") : "MM/DD/YYYY";
                            })()}
                            <Calendar className="ml-auto h-4 w-4 opacity-70" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-0 w-[320px] sm:w-[360px]" align="start">
                          <DobPicker
                            value={form.dob}
                            onChange={(iso) => setForm((f) => ({ ...f, dob: iso }))}
                          />
                        </PopoverContent>
                      </Popover>
                      </div>
                        </div>

                  <div className="space-y-2">
                    <Label className="text-gray-800">Height</Label>
                    {form.heightUnit === "metric" ? (
                      <div className="flex items-center gap-3">
                        <div className="relative flex-1">
                          <input
                            type="number"
                            min={1}
                            step={1}
                            placeholder="180"
                            value={form.heightCm}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, heightCm: e.target.value }))
                            }
                            className="bg-white border border-gray-300 text-gray-900 placeholder:text-gray-500 pr-14 rounded-md px-3 py-2 w-full"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                            cm
                          </span>
                      </div>
                        <ToggleGroup
                          type="single"
                          value={form.heightUnit}
                          onValueChange={(v) =>
                            v &&
                            setForm((f) => ({
                              ...f,
                              heightUnit: v as "imperial" | "metric",
                            }))
                          }
                          className="w-[120px] justify-center bg-gray-100 border border-gray-300 rounded-md"
                        >
                          <ToggleGroupItem value="imperial" className="px-3 py-2 text-xs">
                            ft/in
                          </ToggleGroupItem>
                          <ToggleGroupItem value="metric" className="px-3 py-2 text-xs">
                            cm
                          </ToggleGroupItem>
                        </ToggleGroup>
                    </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-3 items-center">
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            step={1}
                            placeholder="5"
                            value={form.heightFeet}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, heightFeet: e.target.value }))
                            }
                            className="w-full bg-white border border-gray-300 text-gray-900 placeholder:text-gray-500 pr-10 rounded-md px-3 py-2"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                            ft
                          </span>
                  </div>
                        <div className="relative">
                          <input
                            type="number"
                            min={0}
                            max={11}
                            step={1}
                            placeholder="10"
                            value={form.heightInches}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, heightInches: e.target.value }))
                            }
                            className="w-full bg-white border border-gray-300 text-gray-900 placeholder:text-gray-500 pr-10 rounded-md px-3 py-2"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                            in
                          </span>
                        </div>
                        <ToggleGroup
                          type="single"
                          value={form.heightUnit}
                          onValueChange={(v) =>
                            v &&
                            setForm((f) => ({
                              ...f,
                              heightUnit: v as "imperial" | "metric",
                            }))
                          }
                          className="w-[120px] justify-self-end flex justify-center bg-gray-100 border border-gray-300 rounded-md"
                        >
                          <ToggleGroupItem value="imperial" className="px-3 py-2 text-xs">
                            ft/in
                          </ToggleGroupItem>
                          <ToggleGroupItem value="metric" className="px-3 py-2 text-xs">
                            cm
                          </ToggleGroupItem>
                        </ToggleGroup>
                      </div>
                    )}
                </div>
                
                  <div className="space-y-2">
                    <Label className="text-gray-800">Weight</Label>
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1">
                        <input
                          type="number"
                          min={1}
                          step={1}
                          placeholder={form.weightUnit === "kg" ? "82" : "180"}
                          value={form.weightValue}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, weightValue: e.target.value }))
                          }
                            className="bg-white border border-gray-300 text-gray-900 placeholder:text-gray-500 pr-14 rounded-md px-3 py-2 w-full"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">
                          {form.weightUnit === "kg" ? "kg" : "lbs"}
                        </span>
                      </div>
                      <ToggleGroup
                        type="single"
                        value={form.weightUnit}
                        onValueChange={(v) =>
                          v &&
                          setForm((f) => ({ ...f, weightUnit: v as "lb" | "kg" }))
                        }
                        className="w-[120px] justify-center bg-gray-100 border border-gray-300 rounded-md"
                      >
                        <ToggleGroupItem value="lb" className="px-3 py-2 text-xs">
                          lbs
                        </ToggleGroupItem>
                        <ToggleGroupItem value="kg" className="px-3 py-2 text-xs">
                          kg
                        </ToggleGroupItem>
                      </ToggleGroup>
                    </div>
                    </div>
                  </div>
                )}
            </CardContent>
          </Card>

          {/* Motivations */}
          <Card className="border border-gray-200 bg-white text-gray-900">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Target className="w-5 h-5 mr-2 text-primary" />
                Motivations
              </CardTitle>
              {onboardingEdit && (
                <div className="text-xs text-gray-600">
                  Select up to 2 motivations ({form.motivations.length}/2)
                </div>
              )}
            </CardHeader>
            <CardContent>
              {!onboardingEdit ? (
                    <div className="flex flex-wrap gap-2">
                  {onboardingSummary.motivations.map((m) => (
                    <Badge key={m} className="bg-primary/10 text-gray-900 px-3 py-1">
                      {labels.motivation[m] ?? m}
                    </Badge>
                      ))}
                    </div>
              ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {motivationOptions.map((opt) => {
                    const checked = form.motivations.includes(opt.id);
                    const disableNew = !checked && form.motivations.length >= 2;
                    return (
                      <label
                        key={opt.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-xl border",
                          checked
                            ? "border-primary bg-primary/10"
                            : "border-gray-200 bg-gray-50",
                          disableNew && "opacity-50 cursor-not-allowed"
                        )}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            setForm((f) => {
                              const current = new Set(f.motivations);
                              if (v) {
                                if (
                                  current.size >= 2 &&
                                  !current.has(opt.id)
                                )
                                  return f;
                                current.add(opt.id);
                              } else {
                                current.delete(opt.id);
                              }
                              return {
                                ...f,
                                motivations: Array.from(current),
                              };
                            });
                          }}
                          disabled={disableNew}
                        />
                        <span className="text-gray-900">{opt.label}</span>
                      </label>
                    );
                  })}
                          </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Level */}
          <Card className="border border-gray-200 bg-white text-gray-900">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Activity className="w-5 h-5 mr-2 text-primary" />
                Activity Level
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!onboardingEdit ? (
                <div className="space-y-2">
                  <div className="font-medium">
                    {onboardingSummary.activityLevel ? labels.activityLevel[onboardingSummary.activityLevel] : "Not specified"}
                  </div>
                  {onboardingSummary.activityLevel && activityLevelOptions.find(opt => opt.value === onboardingSummary.activityLevel) && (
                    <div className="text-sm text-gray-600">
                      {activityLevelOptions.find(opt => opt.value === onboardingSummary.activityLevel)?.description}
                    </div>
                  )}
                </div>
              ) : (
                <RadioGroup
                  value={form.activityLevel}
                  onValueChange={(v) => setForm((f) => ({ ...f, activityLevel: v }))}
                  className="grid grid-cols-1 gap-3"
                >
                  {activityLevelOptions.map((opt) => (
                    <label
                      key={opt.value}
                      className={cn(
                        "flex flex-col p-4 border-2 rounded-xl cursor-pointer transition-all bg-gray-50",
                        form.activityLevel === opt.value
                          ? "border-primary shadow-neon-glow"
                          : "border-primary/20 hover:border-primary/50"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value={opt.value} />
                          <div className="font-medium">{opt.label}</div>
                        </div>
                        <div
                          className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center",
                            form.activityLevel === opt.value
                              ? "bg-primary text-black"
                              : "bg-white border border-gray-300"
                          )}
                        >
                          {form.activityLevel === opt.value ? "✓" : ""}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 mt-2 ml-8">
                        {opt.description}
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              )}
            </CardContent>
          </Card>

          {/* Workout Frequency */}
          <Card className="border border-gray-200 bg-white text-gray-900">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-primary" />
                Workout Frequency
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!onboardingEdit ? (
                <div className="space-y-2">
                  <div className="font-medium">
                    {onboardingSummary.workoutFrequency
                      ? (labels.workoutFrequency as Record<string, string>)[onboardingSummary.workoutFrequency] ?? "Not specified"
                      : "Not specified"}
                  </div>
                  {onboardingSummary.workoutDuration && (
                    <div className="text-sm text-gray-600">
                      Duration: {(labels.workoutDuration as Record<string, string>)[onboardingSummary.workoutDuration] ?? onboardingSummary.workoutDuration}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <RadioGroup
                    value={form.workoutFrequency}
                    onValueChange={(v) =>
                      setForm((f) => ({ ...f, workoutFrequency: v }))
                    }
                    className="grid grid-cols-1 gap-3"
                  >
                    {workoutFrequencyOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className={cn(
                          "flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition-all bg-gray-50",
                          form.workoutFrequency === opt.value
                            ? "border-primary shadow-neon-glow"
                            : "border-primary/20 hover:border-primary/50"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <RadioGroupItem value={opt.value} />
                          <div className="font-medium">{opt.label}</div>
                        </div>
                        <div
                          className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center",
                            form.workoutFrequency === opt.value
                              ? "bg-primary text-black"
                              : "bg-white border border-gray-300"
                          )}
                        >
                          {form.workoutFrequency === opt.value ? "✓" : ""}
                        </div>
                      </label>
                    ))}
                  </RadioGroup>
                  {form.workoutFrequency && (
                    <div className="space-y-2">
                      <Label className="text-gray-800">Workout Duration</Label>
                      <RadioGroup
                        value={form.workoutDuration}
                        onValueChange={(v) => setForm((f) => ({ ...f, workoutDuration: v }))}
                        className="grid grid-cols-3 gap-3"
                      >
                        {workoutDurationOptions.map((opt) => (
                          <label
                            key={opt.value}
                            className={cn(
                              "flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all bg-gray-50",
                              form.workoutDuration === opt.value
                                ? "border-primary shadow-neon-glow"
                                : "border-primary/20 hover:border-primary/50"
                            )}
                          >
                            <RadioGroupItem value={opt.value} className="sr-only" />
                            <span className="font-medium">{opt.label}</span>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Exercise Types */}
          <Card className="border border-gray-200 bg-white text-gray-900">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Activity className="w-5 h-5 mr-2 text-primary" />
                Exercise Types You Enjoy
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!onboardingEdit ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {onboardingSummary.exerciseTypes.map((t) => (
                    <div
                      key={t}
                      className="p-3 rounded-lg border-2 text-center border-gray-200 bg-gray-50"
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {labels.exerciseType[t] ?? t}
                      </div>
                    </div>
                      ))}
                    </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {exerciseTypeOptions.map((opt) => {
                    const checked = form.exerciseTypes.includes(opt.id);
                    return (
                      <label
                        key={opt.id}
                        className={cn(
                          "flex items-center gap-3 p-3 rounded-xl border",
                          checked
                            ? "border-primary bg-primary/10"
                            : "border-gray-200 bg-gray-50"
                        )}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) => {
                            setForm((f) => {
                              const set = new Set(f.exerciseTypes);
                              if (v) set.add(opt.id);
                              else set.delete(opt.id);
                              const nextExerciseTypes = Array.from(set);
                              return {
                                ...f,
                                exerciseTypes: nextExerciseTypes,
                                sportsEnjoyed: nextExerciseTypes.includes("sports") ? f.sportsEnjoyed : "",
                              };
                            });
                          }}
                        />
                        <span className="text-gray-900">{opt.label}</span>
                      </label>
                    );
                  })}
                  </div>
                )}

              {(onboardingEdit ? form.exerciseTypes.includes("sports") : onboardingSummary.exerciseTypes.includes("sports")) && (
                <div className="mt-4 space-y-2">
                  <Label className="text-gray-900">Sports you enjoy</Label>
                  {!onboardingEdit ? (
                    <div className="text-sm text-gray-600">
                      {onboardingSummary.sportsEnjoyed?.trim() ? onboardingSummary.sportsEnjoyed : "Not specified"}
                    </div>
                  ) : (
                    <Textarea
                      value={form.sportsEnjoyed}
                      onChange={(e) => setForm((f) => ({ ...f, sportsEnjoyed: e.target.value }))}
                      placeholder="e.g., Soccer, tennis, basketball"
                      className="min-h-[90px]"
                    />
                  )}
                </div>
              )}
            </CardContent>
          </Card>

                {/* Exercise variety */}
          <Card className="border border-gray-200 bg-white text-gray-900">
            <CardHeader>
              <CardTitle className="flex items-center">
                Exercise variety
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!onboardingEdit ? (
                <div className="text-sm text-gray-600 whitespace-pre-wrap">
                  {onboardingSummary.variety?.trim() ? onboardingSummary.variety : "Not specified"}
                </div>
              ) : (
                <VarietyField
                  id="trainer-onboarding-variety"
                  value={form.variety}
                  onChange={(next) => setForm((f) => ({ ...f, variety: next }))}
                  label="Exercise variety"
                  labelClassName="text-gray-900"
                />
              )}
            </CardContent>
          </Card>

                {/* Timeline */}
          <Card className="border border-gray-200 bg-white text-gray-900">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="w-5 h-5 mr-2 text-primary" />
                Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!onboardingEdit ? (
                <div className="font-medium">
                  {onboardingSummary.timeline
                    ? (labels.timeframe as Record<string, string>)[onboardingSummary.timeline] ?? "Not specified"
                    : "Not specified"}
                  </div>
              ) : (
                <RadioGroup
                  value={form.timeline}
                  onValueChange={(v) => setForm((f) => ({ ...f, timeline: v }))}
                  className="grid grid-cols-1 gap-3"
                >
                  {timeframeOptions.map((opt) => (
                    <label
                      key={opt.value}
                      className={cn(
                        "flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition-all bg-gray-50",
                        form.timeline === opt.value
                          ? "border-primary shadow-neon-glow"
                          : "border-primary/20 hover:border-primary/50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value={opt.value} />
                        <div className="font-medium">{opt.label}</div>
                  </div>
                      <div
                        className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center",
                          form.timeline === opt.value
                            ? "bg-primary text-black"
                            : "bg-white border border-gray-200"
                        )}
                      >
                        {form.timeline === opt.value ? "✓" : ""}
                      </div>
                    </label>
                  ))}
                </RadioGroup>
              )}
            </CardContent>
          </Card>

          {/* Event Training */}
          <Card className="border border-gray-200 bg-white text-gray-900">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Trophy className="w-5 h-5 mr-2 text-primary" />
                Event Training
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!onboardingEdit ? (
                <div className="space-y-4">
                  {onboardingSummary.trainingForEvent === "yes" ? (
                    <>
                      <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <Trophy className="w-5 h-5 text-primary" />
                          </div>
                          <div className="font-medium">Training for an Event</div>
                        </div>
                        {onboardingSummary.eventDetails && (
                          <div className="mb-3">
                            <div className="text-xs text-gray-600 mb-1">Event Details</div>
                            <div className="text-gray-900">{onboardingSummary.eventDetails}</div>
                          </div>
                        )}
                        {onboardingSummary.eventDate && (
                          <div className="flex items-center gap-2">
                            <CalendarDays className="w-4 h-4 text-gray-600" />
                            <div>
                              <div className="text-xs text-gray-600">Event Date</div>
                              <div className="text-gray-900 font-medium">
                                {format(parseYYYYMMDDToLocalDate(onboardingSummary.eventDate), "MMMM d, yyyy")}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  ) : onboardingSummary.trainingForEvent === "no" ? (
                    <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                      <div className="text-gray-600">Not training for a specific event</div>
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                      <div className="text-gray-600">No event information provided</div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <RadioGroup
                    value={form.trainingForEvent || ""}
                    onValueChange={(value) => {
                      setForm((f) => {
                        const newForm = { ...f, trainingForEvent: value };
                        // Clear event details and date if user selects "No"
                        if (value === "no") {
                          newForm.eventDetails = "";
                          newForm.eventDate = "";
                        }
                        return newForm;
                      });
                    }}
                    className="grid grid-cols-2 gap-4"
                  >
                    <div>
                      <RadioGroupItem value="yes" id="event-yes-admin" className="peer sr-only" />
                      <Label
                        htmlFor="event-yes-admin"
                        className={cn(
                          "flex flex-col items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all bg-gray-50",
                          form.trainingForEvent === "yes"
                            ? "border-primary shadow-neon-glow"
                            : "border-primary/20 hover:border-primary/50"
                        )}
                      >
                        <span className="text-2xl mb-1">✅</span>
                        <span className="font-medium">Yes</span>
                      </Label>
                    </div>
                    <div>
                      <RadioGroupItem value="no" id="event-no-admin" className="peer sr-only" />
                      <Label
                        htmlFor="event-no-admin"
                        className={cn(
                          "flex flex-col items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all bg-gray-50",
                          form.trainingForEvent === "no"
                            ? "border-primary shadow-neon-glow"
                            : "border-primary/20 hover:border-primary/50"
                        )}
                      >
                        <span className="text-2xl mb-1">❌</span>
                        <span className="font-medium">No</span>
                      </Label>
                    </div>
                  </RadioGroup>

                  {form.trainingForEvent === "yes" && (
                    <div className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="event-details-admin" className="text-gray-800">Event Details</Label>
                        <Textarea
                          id="event-details-admin"
                          placeholder="e.g., Marathon in Boston, Powerlifting competition, Triathlon, etc."
                          value={form.eventDetails || ""}
                          onChange={(e) => setForm((f) => ({ ...f, eventDetails: e.target.value }))}
                          className="bg-white border border-gray-300 text-gray-900 placeholder:text-gray-500 min-h-[100px]"
                        />
                        <p className="text-xs text-gray-600">Describe the event you're training for</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-gray-800">Event Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal bg-white border-gray-300 text-gray-900",
                                !form.eventDate && "text-gray-500"
                              )}
                            >
                              {form.eventDate ? (
                                format(parseYYYYMMDDToLocalDate(form.eventDate), "MMMM d, yyyy")
                              ) : (
                                "Select event date"
                              )}
                              <CalendarDays className="ml-auto h-4 w-4 opacity-70" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="p-0 w-auto" align="start">
                            <DateRangeCalendar
                              mode="single"
                              selected={form.eventDate ? parseYYYYMMDDToLocalDate(form.eventDate) : undefined}
                              onSelect={(date) => {
                                if (date) {
                                  setForm((f) => ({ ...f, eventDate: format(date, "yyyy-MM-dd") }));
                                }
                              }}
                              disabled={(date) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                return date < today;
                              }}
                              className="bg-white text-gray-900 border border-gray-200"
                            />
                          </PopoverContent>
                        </Popover>
                        <p className="text-xs text-gray-600">When is your event?</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workouts">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex flex-row items-center justify-between mb-4">
                <CardTitle>Workouts</CardTitle>
                <div className="flex items-center gap-3">
                  {workoutDisplayMode === "list" && (
                    <ToggleGroup
                      type="single"
                      value={workoutView}
                      onValueChange={(v) => v && setWorkoutView(v as "history" | "scheduled")}
                      className="bg-gray-100 rounded-md p-1"
                    >
                      <ToggleGroupItem
                        value="history"
                        className="px-3 py-1 text-sm font-medium rounded-md border data-[state=on]:bg-primary data-[state=on]:text-black data-[state=on]:border-primary data-[state=off]:bg-white data-[state=off]:text-gray-700 data-[state=off]:border-transparent data-[state=off]:hover:bg-gray-200"
                      >
                        History
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value="scheduled"
                        className="px-3 py-1 text-sm font-medium rounded-md border data-[state=on]:bg-primary data-[state=on]:text-black data-[state=on]:border-primary data-[state=off]:bg-white data-[state=off]:text-gray-700 data-[state=off]:border-transparent data-[state=off]:hover:bg-gray-200"
                      >
                        Scheduled
                      </ToggleGroupItem>
                    </ToggleGroup>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="bg-gradient-to-r from-primary to-lime-hover"
                    onClick={() => {
                      setAssignWorkoutDialogOpen(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Assign Workout
                  </Button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                {workoutDisplayMode === "calendar" ? (
                  <div className="flex items-center justify-center gap-2 flex-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-gray-100 hover:bg-gray-200 border-gray-300"
                      onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                    >
                      <ChevronLeft className="w-4 h-4 text-gray-700" />
                    </Button>
                    <span className="text-lg font-semibold text-gray-900 min-w-[180px] text-center">
                      {format(calendarMonth, "MMMM yyyy")}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-gray-100 hover:bg-gray-200 border-gray-300"
                      onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                    >
                      <ChevronRight className="w-4 h-4 text-gray-700" />
                    </Button>
                  </div>
                ) : (
                  <div />
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <ToggleGroup
                    type="single"
                    value={workoutDisplayMode}
                    onValueChange={(v) => v && setWorkoutDisplayMode(v as "list" | "calendar")}
                    className="bg-gray-100 rounded-md p-1"
                  >
                    <ToggleGroupItem
                      value="list"
                      className="px-3 py-1 text-sm font-medium rounded-md border data-[state=on]:bg-primary data-[state=on]:text-black data-[state=on]:border-primary data-[state=off]:bg-white data-[state=off]:text-gray-700 data-[state=off]:border-transparent data-[state=off]:hover:bg-gray-200"
                    >
                      <FileText className="w-4 h-4" />
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="calendar"
                      className="px-3 py-1 text-sm font-medium rounded-md border data-[state=on]:bg-primary data-[state=on]:text-black data-[state=on]:border-primary data-[state=off]:bg-white data-[state=off]:text-gray-700 data-[state=off]:border-transparent data-[state=off]:hover:bg-gray-200"
                    >
                      <Calendar className="w-4 h-4" />
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>
              {workoutDisplayMode === "calendar" && (
                <div className="flex items-center gap-4 mt-2 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300"></div>
                    <span className="text-emerald-700">Completed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-blue-100 border border-blue-300"></div>
                    <span className="text-blue-700">Scheduled</span>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              {workoutDisplayMode === "calendar" ? (
                (() => {
                  // Get workouts for the current month
                  const monthStart = startOfMonth(calendarMonth);
                  const monthEnd = endOfMonth(calendarMonth);
                  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
                  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
                  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

                  return (
                    <div className="space-y-4">
                      {/* Calendar Grid */}
                      <div className="grid grid-cols-7 gap-1">
                        {/* Day headers */}
                        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                          <div key={day} className="text-center text-xs font-medium text-gray-600 py-2">
                            {day}
                          </div>
                        ))}
                        
                        {/* Calendar days */}
                        {calendarDays.map((day, idx) => {
                          const dateKey = format(day, "yyyy-MM-dd");
                          const dayWorkouts = workoutsByDate.get(dateKey) || [];
                          const isCurrentMonth = isSameMonth(day, calendarMonth);
                          const isToday = isSameDay(day, new Date());
                          const isSelected = selectedDate && isSameDay(day, selectedDate);

                          return (
                            <div
                              key={idx}
                              onClick={() => {
                                setSelectedDate(day);
                                if (dayWorkouts.length === 0) {
                                  setAssignWorkoutDialogOpen(true);
                                }
                              }}
                              className={`min-h-[80px] border border-gray-200 rounded p-1 cursor-pointer transition-all hover:border-primary hover:shadow-sm ${
                                !isCurrentMonth ? "bg-gray-50 opacity-50" : "bg-white"
                              } ${isToday ? "ring-2 ring-primary" : ""} ${
                                isSelected ? "ring-2 ring-blue-500 border-blue-500" : ""
                              }`}
                            >
                              <div className={`text-xs font-medium mb-1 ${isCurrentMonth ? "text-gray-900" : "text-gray-400"}`}>
                                {format(day, "d")}
                              </div>
                              <div className="space-y-1">
                                {dayWorkouts.slice(0, 2).map((workout) => (
                                  <div
                                    key={workout.id}
                                    className={`text-xs px-1 py-0.5 rounded truncate ${
                                      workout.status === "completed"
                                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                                        : workout.status === "incomplete"
                                        ? "bg-gray-100 text-gray-800 border border-gray-300"
                                        : "bg-blue-100 text-blue-800 border border-blue-300"
                                    }`}
                                    title={workout.name}
                                  >
                                    {workout.name}
                                  </div>
                                ))}
                                {dayWorkouts.length > 2 && (
                                  <div className="text-xs text-gray-500 px-1">
                                    +{dayWorkouts.length - 2} more
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Workouts List Below Calendar */}
                      <div className="mt-6">
                        {selectedDate ? (
                          <>
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-lg font-semibold text-gray-900">
                                Workouts for {format(selectedDate, "MMM d, yyyy")}
                              </h3>
                              <Button
                                variant="outline"
                                size="sm"
                                className="bg-gradient-to-r from-primary to-lime-hover"
                                onClick={() => {
                                  if (selectedDate) {
                                    setAssignWorkoutDialogOpen(true);
                                  }
                                }}
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Assign Workout
                              </Button>
                            </div>
                            <div className="space-y-3">
                              {(() => {
                                if (!selectedDate) return null;
                                
                                const selectedDateStr = format(selectedDate, "yyyy-MM-dd");
                                const workoutsToShow = workoutAssignments.filter((w) => w.date === selectedDateStr);

                                if (workoutsToShow.length === 0) {
                                  return (
                                    <div className="text-center py-8 text-gray-500">
                                      No workouts for {format(selectedDate, "MMM d, yyyy")}
                                    </div>
                                  );
                                }

                            return workoutsToShow.map((workout) => {
                              const isCompleted = workout.completed_workout !== null;
                              const workoutDate = parseYYYYMMDDToLocalDate(workout.date);
                              workoutDate.setHours(0, 0, 0, 0);
                              const isPast = workoutDate < today;
                              
                              // Get all exercises from groupings
                              const allExercises = workout.groupings.flatMap((g) => g.exercises);
                              
                              return (
                                <Card key={workout.id} className="border border-gray-200">
                                  <CardContent className="p-4">
                                    <div className="flex justify-between items-start mb-2">
                                      <div className="flex-1">
                                        <h4 className="font-semibold text-gray-900">{workout.name}</h4>
                                        <p className="text-sm text-gray-600 mt-1">
                                          {format(parseYYYYMMDDToLocalDate(workout.date), "MMM d, yyyy")}
                                        </p>
                                        {workout.description && (
                                          <p className="text-xs text-gray-500 mt-1">{workout.description}</p>
                                        )}
                                        {isCompleted && workout.completed_workout && (
                                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                                            <span>{workout.completed_workout.minutes}min</span>
                                            <span>{workout.completed_workout.points} pts</span>
                                            <span>Intensity: {workout.completed_workout.intensity}/10</span>
                                            <span>Energy: {workout.completed_workout.energy}/10</span>
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex flex-col items-end gap-2">
                                        {isCompleted ? (
                                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                                            Completed
                                          </Badge>
                                        ) : isPast ? (
                                          <Badge className="bg-gray-100 text-gray-800 border-gray-300">
                                            Incomplete
                                          </Badge>
                                        ) : (
                                          <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                                            Scheduled
                                          </Badge>
                                        )}
                                        {!isPast && (
                                          <div className="flex items-center gap-2">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => {
                                                setWorkoutToEditWithAI(workout);
                                                setEditWorkoutWithAIOpen(true);
                                              }}
                                            >
                                              <Zap className="w-4 h-4 mr-1" />
                                              Edit with AI
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => {
                                                const assignmentDate = parseYYYYMMDDToLocalDate(workout.date);
                                                if (onEditWorkout) {
                                                  onEditWorkout(workout.id, clientId, assignmentDate);
                                                }
                                              }}
                                            >
                                              <Edit className="w-4 h-4 mr-1" />
                                              Edit
                                            </Button>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                              onClick={() => {
                                                setWorkoutToDelete({ id: workout.id, name: workout.name });
                                                setDeleteWorkoutDialogOpen(true);
                                              }}
                                            >
                                              <Trash2 className="w-4 h-4 mr-1" />
                                              Delete
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {allExercises.length > 0 && (
                                      <Collapsible defaultOpen={false}>
                                        <CollapsibleTrigger className="w-full text-left text-sm font-medium text-gray-700 flex items-center gap-1 py-2 hover:underline">
                                          <ChevronRight className="h-4 w-4 data-[state=open]:hidden" />
                                          <ChevronDown className="h-4 w-4 hidden data-[state=open]:block" />
                                          View exercises ({allExercises.length})
                                        </CollapsibleTrigger>
                                        <CollapsibleContent>
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                                            {allExercises.map((exercise) => (
                                              <div key={exercise.id} className="bg-gray-50 p-3 rounded text-sm">
                                                <div className="font-medium text-gray-900">
                                                  {exercise.exercise_name}
                                                </div>
                                                <div className="text-gray-600">
                                                  {exercise.sets} sets × {exercise.reps} reps
                                                  {exercise.weight_lbs && exercise.weight_lbs > 0 && ` @ ${exercise.weight_lbs}lbs`}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </CollapsibleContent>
                                      </Collapsible>
                                    )}
                                  </CardContent>
                                </Card>
                              );
                            });
                          })()}
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-12">
                            <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                            <p className="text-gray-500 text-lg font-medium mb-2">No date selected</p>
                            <p className="text-gray-400 text-sm">Click on a date in the calendar to view workouts</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()
              ) : workoutView === "history" ? (
                <div className="space-y-4">
                  {loadingWorkouts ? (
                    <div className="text-center py-8 text-gray-500">Loading workouts...</div>
                  ) : historyWorkouts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No workout history.</div>
                  ) : (
                    historyWorkouts.map((workout) => {
                      const isCompleted = workout.completed_workout !== null;
                      const allExercises = workout.groupings.flatMap((g) => g.exercises);
                      const statusLabels: Record<string, string> = {
                        R: "Ready",
                        S: "Supercharged",
                        T: "Tired",
                        L: "Low",
                      };
                      
                      return (
                        <Card key={workout.id} className="border border-gray-200">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <h4 className="font-semibold text-lg text-gray-900">
                                  {workout.name}
                                </h4>
                                <div className="flex items-center gap-2">
                                  <p className="text-gray-600 text-sm">
                                    {formatDate(workout.date)}
                                  </p>
                                  {isCompleted && workout.completed_workout && (
                                    <span
                                      className={`text-xs px-2 py-0.5 rounded-full ${
                                        workout.completed_workout.status === "S"
                                          ? "bg-yellow-100 text-yellow-800"
                                          : workout.completed_workout.status === "T"
                                          ? "bg-orange-100 text-orange-800"
                                          : workout.completed_workout.status === "L"
                                          ? "bg-red-100 text-red-800"
                                          : "bg-green-100 text-green-800"
                                      }`}
                                    >
                                      {statusLabels[workout.completed_workout.status] || "Ready"}
                                    </span>
                                  )}
                                  {!isCompleted && (
                                    <Badge className="bg-gray-100 text-gray-800 border-gray-300">
                                      Incomplete
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                {isCompleted && workout.completed_workout ? (
                                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                                    <span>{workout.completed_workout.minutes}min</span>
                                    <span>{workout.completed_workout.points} pts</span>
                                  </div>
                                ) : (
                                  <Badge className="bg-gray-100 text-gray-800 border-gray-300">
                                    Incomplete
                                  </Badge>
                                )}
                              </div>
                            </div>
                            {isCompleted && workout.completed_workout && (
                              <>
                                <div className="mb-1 text-sm text-gray-700">
                                  Intensity: {workout.completed_workout.intensity}/10
                                </div>
                                <div className="mb-1 text-sm text-gray-700">
                                  Energy: {workout.completed_workout.energy}/10
                                </div>
                              </>
                            )}

                            {allExercises.length > 0 && (
                              <Collapsible defaultOpen={false}>
                                <CollapsibleTrigger className="w-full text-left text-sm font-medium text-gray-700 flex items-center gap-1 py-2 hover:underline">
                                  <ChevronRight className="h-4 w-4 data-[state=open]:hidden" />
                                  <ChevronDown className="h-4 w-4 hidden data-[state=open]:block" />
                                  View exercises ({allExercises.length})
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                                    {allExercises.map((exercise) => (
                                      <div key={exercise.id} className="bg-gray-50 p-3 rounded text-sm">
                                        <div className="font-medium text-gray-900">
                                          {exercise.exercise_name}
                                        </div>
                                        <div className="text-gray-600">
                                          {exercise.sets} sets × {exercise.reps} reps
                                          {exercise.weight_lbs && exercise.weight_lbs > 0 && ` @ ${exercise.weight_lbs}lbs`}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {loadingWorkouts ? (
                    <div className="text-center py-8 text-gray-500">Loading workouts...</div>
                  ) : scheduledWorkouts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No scheduled workouts.</div>
                  ) : (
                    scheduledWorkouts.map((workout) => {
                      const allExercises = workout.groupings.flatMap((g) => g.exercises);
                      
                      return (
                        <Card key={workout.id} className="border border-gray-200">
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <h4 className="font-semibold text-lg text-gray-900">
                                  {workout.name}
                                </h4>
                                {workout.description && (
                                  <p className="text-gray-600 text-sm mt-1">{workout.description}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">Assigned: {formatDate(workout.date)}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary">Scheduled</Badge>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setWorkoutToEditWithAI(workout);
                                    setEditWorkoutWithAIOpen(true);
                                  }}
                                >
                                  <Zap className="w-4 h-4 mr-1" />
                                  Edit with AI
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const assignmentDate = parseYYYYMMDDToLocalDate(workout.date);
                                    if (onEditWorkout) {
                                      onEditWorkout(workout.id, clientId, assignmentDate);
                                    }
                                  }}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => {
                                    setWorkoutToDelete({ id: workout.id, name: workout.name });
                                    setDeleteWorkoutDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                            {allExercises.length > 0 && (
                              <Collapsible defaultOpen={false}>
                                <CollapsibleTrigger className="w-full text-left text-sm font-medium text-gray-700 flex items-center gap-1 py-2 hover:underline">
                                  <ChevronRight className="h-4 w-4 data-[state=open]:hidden" />
                                  <ChevronDown className="h-4 w-4 hidden data-[state=open]:block" />
                                  View exercises ({allExercises.length})
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                                    {allExercises.map((exercise) => (
                                      <div key={exercise.id} className="bg-gray-50 p-3 rounded text-sm">
                                        <div className="font-medium text-gray-900">
                                          {exercise.exercise_name}
                                        </div>
                                        <div className="text-gray-600">
                                          {exercise.sets} sets × {exercise.reps} reps
                                          {exercise.weight_lbs && exercise.weight_lbs > 0 && ` @ ${exercise.weight_lbs}lbs`}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </CollapsibleContent>
                              </Collapsible>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="checklist">
          <Card className="border border-gray-200 bg-white">
            <CardHeader>
              <CardTitle className="flex items-center text-gray-900">
                <CheckCircle2 className="w-5 h-5 mr-2 text-primary" />
                Checklist
              </CardTitle>
              <p className="text-gray-600">
                Key steps to get this client fully set up and engaged
              </p>
            </CardHeader>
            <CardContent>
              {checklistLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-gray-600">Loading checklist...</div>
                </div>
              ) : checklistItems.length === 0 ? (
                <div className="text-center py-8 text-gray-600">
                  No checklist items assigned yet.
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-900">Progress</span>
                    <span className="text-sm text-gray-600">
                      {checklistItems.filter(item => item.is_completed).length} / {checklistItems.length} completed
                    </span>
                  </div>
                  
                  <Progress 
                    value={checklistItems.length > 0 ? (checklistItems.filter(item => item.is_completed).length / checklistItems.length) * 100 : 0} 
                    className="h-2 mb-6"
                  />
                  
                  {checklistItems.map((item) => (
                    <div key={item.id} className={`flex items-center p-4 border rounded-lg ${item.is_completed ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mr-3 ${item.is_completed ? 'bg-emerald-500 text-white' : 'bg-white text-gray-500 border border-gray-300'}`}>
                        {item.is_completed ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <span className="text-gray-500 text-xs font-bold">{item.checklist_item.order}</span>
                        )}
                      </div>
                      <div className="flex-grow">
                        <span className={`font-medium ${item.is_completed ? 'text-gray-900' : 'text-gray-700'}`}>
                          {item.checklist_item.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        {item.checklist_item.type === "assessment" && item.checklist_item.assessment_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-gray-300 text-gray-900 hover:bg-gray-100"
                            onClick={() => navigate(`/assessments/${item.checklist_item.assessment_id}`)}
                          >
                            Open assessment
                          </Button>
                        )}
                        {item.is_completed ? (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="border-gray-300 text-gray-900 hover:bg-gray-100"
                            onClick={() => handleIncompleteChecklistItem(item.id)}
                            disabled={completingItemId === item.id}
                          >
                            {completingItemId === item.id ? "Updating..." : "Mark incomplete"}
                          </Button>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="border-gray-300 text-gray-900 hover:bg-gray-100"
                            onClick={() => handleCompleteChecklistItem(item.id)}
                            disabled={completingItemId === item.id}
                          >
                            {completingItemId === item.id ? "Completing..." : "Mark complete"}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="equipment">
          {/* Workout Location */}
          <Card className="border border-gray-200 bg-white mb-6">
            <CardHeader>
              <CardTitle className="flex items-center text-gray-900">
                <Activity className="w-5 h-5 mr-2 text-primary" />
                Workout location
              </CardTitle>
              <p className="text-gray-600">
                Where this client usually trains
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              {workoutLocations.length === 0 && !locationEdit && (
                <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="text-gray-600">unset</div>
                  <Button
                    variant="outline"
                    className="border-gray-300 text-gray-900 hover:bg-gray-100"
                    onClick={() => setLocationEdit(true)}
                  >
                    Set location
                  </Button>
                </div>
              )}
              {workoutLocations.length > 0 && !locationEdit && (
                <div className="flex items-center justify-between gap-4 p-4 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    {workoutLocations.map((loc) => (
                      <Badge key={loc} variant="secondary" className="text-xs">
                        {loc}
                      </Badge>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    className="border-gray-300 text-gray-900 hover:bg-gray-100"
                    onClick={() => setLocationEdit(true)}
                  >
                    Edit
                  </Button>
                </div>
              )}
              {locationEdit && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3">
                    {["At Home", "Commercial Gym", "Outdoor / Park", "Mixed"].map((locationOption) => {
                      const checked = workoutLocations.includes(locationOption);
                      return (
                        <label
                          key={locationOption}
                          className={cn(
                            "flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all",
                            checked
                              ? "border-primary bg-primary/10"
                              : "border-gray-200 bg-white hover:border-primary/50"
                          )}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                              if (v) {
                                setWorkoutLocations((prev) => [...prev, locationOption]);
                              } else {
                                setWorkoutLocations((prev) => prev.filter((loc) => loc !== locationOption));
                              }
                            }}
                          />
                          <span className="text-gray-900">{locationOption}</span>
                        </label>
                      );
                    })}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      className="bg-emerald-600 text-white hover:bg-emerald-700"
                      onClick={() => {
                        saveLocation(workoutLocations);
                        setLocationEdit(false);
                      }}
                      disabled={workoutLocations.length === 0}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      className="border-gray-300 text-gray-900 hover:bg-gray-100"
                      onClick={() => {
                        // Reset to current saved location from API
                        setWorkoutLocations(locationCodesToDisplayList(clientProfileData?.onboarding.location));
                        setLocationEdit(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Equipment Access */}
          <Card className="border border-gray-200 bg-white">
            <CardHeader>
              <CardTitle className="flex items-center text-gray-900">
                <Dumbbell className="w-5 h-5 mr-2 text-primary" />
                Equipment access
              </CardTitle>
              <p className="text-gray-600">
                Select the equipment this client has access to
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  {equipmentLoading
                    ? "Loading equipment..."
                    : `${equipment.length} items available`}
                </div>
                {!equipmentEdit ? (
                  <Button
                    variant="outline"
                    className="border-gray-300 text-gray-900 hover:bg-gray-100"
                    onClick={() => setEquipmentEdit(true)}
                    disabled={equipmentLoading}
                  >
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      className="bg-emerald-600 text-white hover:bg-emerald-700"
                      onClick={() => {
                        saveEquipmentPrefs(selectedEquipmentIds);
                        setEquipmentEdit(false);
                      }}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      className="border-gray-300 text-gray-900 hover:bg-gray-100"
                      onClick={() => {
                        try {
                          const saved = localStorage.getItem(
                            `${equipmentStoragePrefix}_selected`
                          );
                          setSelectedEquipmentIds(
                            saved ? JSON.parse(saved) : []
                          );
                        } catch {
                          setSelectedEquipmentIds([]);
                        }
                        setEquipmentEdit(false);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-3">
                {equipment.map((item) => {
                  const checked = selectedEquipmentIds.includes(item.id);
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-200"
                    >
                      <Checkbox
                        checked={checked}
                        disabled={!equipmentEdit}
                        className="border-gray-400 data-[state=checked]:bg-gray-800 data-[state=checked]:border-gray-800"
                        onCheckedChange={(v) => {
                          if (!equipmentEdit) return;
                          const next = v
                            ? Array.from(
                                new Set([...selectedEquipmentIds, item.id])
                              )
                            : selectedEquipmentIds.filter(
                                (id) => id !== item.id
                              );
                          setSelectedEquipmentIds(next);
                        }}
                      />
                      <img
                        src={"/placeholder.svg"}
                        alt={item.name}
                        className="h-10 w-10 rounded-md border border-gray-200 object-cover"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {item.name}
                        </div>
                        {item.category && (
                          <div className="text-xs text-gray-600">
                            {item.category}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="files">
          <Card className="border border-gray-200 bg-white">
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="flex items-center text-gray-900">
                    <FileText className="w-5 h-5 mr-2 text-primary" />
                    Files
                  </CardTitle>
                  <p className="text-gray-600">
                    Client uploads and files shared by the trainer
                  </p>
                </div>
                <Button
                  className="bg-primary text-black hover:bg-primary/90 rounded-full px-5 py-2"
                  onClick={() => setUploadDialogOpen(true)}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload file
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-8">
              {filesLoading ? (
                <div className="text-gray-600 text-sm">Loading files…</div>
              ) : filesError ? (
                <div className="text-sm text-red-600">Failed to load files.</div>
              ) : (
                (() => {
                  const clientOwned = clientFiles.filter((f) => f.createdByProfileId === clientId);
                  const trainerOwned = clientFiles.filter((f) => f.createdByProfileId !== clientId);

                  const isImageUrl = (url: string | null) => {
                    if (!url) return false;
                    let path = url;
                    try {
                      path = new URL(url, window.location.origin).pathname;
                    } catch {
                      path = url.split("#")[0]?.split("?")[0] ?? url;
                    }
                    const lower = path.toLowerCase();
                    return (
                      lower.endsWith(".jpg") ||
                      lower.endsWith(".jpeg") ||
                      lower.endsWith(".png") ||
                      lower.endsWith(".gif") ||
                      lower.endsWith(".webp")
                    );
                  };

                  const FileIcon = ({ url }: { url: string | null }) => {
                    if (isImageUrl(url)) return <ImageIcon className="h-4 w-4 text-primary" />;
                    return <FileText className="h-4 w-4 text-primary" />;
                  };

                  const doDownload = (url: string | null, name: string | null) => {
                    if (!url) return;
                    const a = document.createElement("a");
                    a.href = url;
                    a.target = "_blank";
                    a.rel = "noopener noreferrer";
                    if (name) a.download = name;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  };

                  const Section = ({
                    title,
                    files,
                    allowDelete,
                  }: {
                    title: string;
                    files: ClientFileDto[];
                    allowDelete: boolean;
                  }) => (
                    <div className="space-y-3">
                      <div className="text-gray-900 font-medium">{title}</div>
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <Table>
                          <TableHeader>
                            <TableRow className="hover:bg-transparent">
                              <TableHead className="text-gray-600">File</TableHead>
                              <TableHead className="text-gray-600">Uploaded</TableHead>
                              <TableHead className="text-right text-gray-600">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {files.length === 0 ? (
                              <TableRow className="hover:bg-transparent">
                                <TableCell colSpan={3} className="text-gray-600 text-sm">
                                  No files yet.
                                </TableCell>
                              </TableRow>
                            ) : (
                              files.map((f) => (
                                <TableRow key={f.id} className="hover:bg-gray-50">
                                  <TableCell className="flex items-center gap-2">
                                    <FileIcon url={f.url} />
                                    <span className="text-gray-900">{f.name || "Untitled file"}</span>
                                  </TableCell>
                                  <TableCell className="text-gray-700">
                                    {f.uploadedAt ? new Date(f.uploadedAt).toLocaleString() : "—"}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="border-gray-300 text-gray-900 hover:bg-gray-100"
                                        disabled={!f.url}
                                        onClick={() => {
                                          setPreviewFile(f);
                                          setPreviewOpen(true);
                                        }}
                                      >
                                        View
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="border-gray-300 text-gray-900 hover:bg-gray-100"
                                        disabled={!f.url}
                                        onClick={() => doDownload(f.url, f.name)}
                                      >
                                        <Download className="h-4 w-4 mr-1" />
                                        Download
                                      </Button>
                                      {allowDelete ? (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="border-gray-300 text-gray-900 hover:bg-gray-100"
                                          onClick={async () => {
                                            await adminDeleteClientFile(clientId, f.id);
                                            await refreshClientFiles();
                                          }}
                                        >
                                          <Trash2 className="h-4 w-4 mr-1" />
                                          Delete
                                        </Button>
                                      ) : null}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  );

                  return (
                    <>
                      <Section title="Client Files" files={clientOwned} allowDelete={false} />
                      <Section title="Trainer Files" files={trainerOwned} allowDelete={true} />
                      <Dialog
                        open={previewOpen}
                        onOpenChange={(open) => {
                          setPreviewOpen(open);
                          if (!open) setPreviewFile(null);
                        }}
                      >
                        <DialogScrollContent className="bg-white text-gray-900 border border-gray-200 max-w-3xl">
                          <DialogHeader className="shrink-0">
                            <DialogTitle>File preview</DialogTitle>
                            <DialogDescription>{previewFile?.name || "Untitled file"}</DialogDescription>
                          </DialogHeader>
                          <DialogBody>
                            {previewFile?.url && isImageUrl(previewFile.url) ? (
                              <img
                                src={previewFile.url}
                                alt={previewFile.name || "File"}
                                className="w-full rounded-md border border-gray-200 object-contain"
                              />
                            ) : (
                              <div className="text-gray-600 text-sm">Preview not available.</div>
                            )}
                          </DialogBody>
                        </DialogScrollContent>
                      </Dialog>
                    </>
                  );
                })()
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="program" className="space-y-6 text-gray-900">
          <ClientProgramTabContent
            clientId={clientId}
            assignedProgram={assignedProgram}
            onEditProgram={() => setEditAssignedProgramDialogOpen(true)}
            onReassignProgram={() => setAssignProgramDialogOpen(true)}
            onNavigateToWorkouts={() => {
              setWorkoutView("scheduled");
              setActiveTab("workouts");
              void refreshWorkoutAssignments();
            }}
            onNavigateToWorkoutGenerator={(phaseId) => {
              const newParams = new URLSearchParams(searchParams);
              newParams.set("clientTab", "workout-generator");
              newParams.set("phaseId", phaseId);
              setSearchParams(newParams, { replace: true });
              setActiveTab("workout-generator");
            }}
            getDifficultyColor={getDifficultyColor}
          />

          <AssignProgramDialog
            open={assignProgramDialogOpen}
            onOpenChange={setAssignProgramDialogOpen}
            clientId={clientId}
            clientName={`${profile.first_name || ""} ${profile.last_name || ""}`.trim()}
            hasAssignedProgram={!!assignedClientProgram}
            onAssigned={handleProgramAssigned}
          />

          <EditAssignedProgramDialog
            open={editAssignedProgramDialogOpen}
            onOpenChange={setEditAssignedProgramDialogOpen}
            clientProgram={assignedClientProgram}
            clientName={`${profile.first_name || ""} ${profile.last_name || ""}`.trim()}
            onSaved={handleAssignedProgramSaved}
          />


        </TabsContent>

        <TabsContent value="notes">
          <Card className="border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Notes</CardTitle>
              <Button
                className="bg-primary text-black hover:bg-primary/90 rounded-full px-4 py-2"
                onClick={openCreateNoteDialog}
              >
                Add note
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {notesLoading ? (
                <div className="text-center text-gray-500 text-sm py-6">
                  Loading notes...
                </div>
              ) : (
                <div className="space-y-3">
                  {notes.map((note) => (
                    <div
                      key={note.id}
                      className="p-3 border border-gray-200 rounded-lg"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          {note.creator_avatar_url && (
                            <Avatar className="w-6 h-6">
                              <AvatarFallback className="text-xs">
                                {note.creator_name?.[0] || "?"}
                              </AvatarFallback>
                            </Avatar>
                          )}
                          <span className="text-xs font-medium text-gray-700">
                            {note.creator_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={
                              note.is_private_note_for_trainer_only
                                ? "border-gray-300 bg-gray-50 text-gray-700 text-[11px]"
                                : "border-emerald-300 bg-emerald-50 text-emerald-800 text-[11px]"
                            }
                          >
                            {note.is_private_note_for_trainer_only
                              ? "Private"
                              : "Shared with client"}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {note.created_at ? formatDate(note.created_at) : ""}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-gray-500 hover:text-gray-800"
                            onClick={() => openEditNoteDialog(note)}
                            aria-label="Edit note"
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-gray-500 hover:text-red-600"
                            onClick={() => setNoteToDelete(note)}
                            aria-label="Delete note"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm text-gray-700 whitespace-pre-wrap">
                        {note.text}
                      </div>
                    </div>
                  ))}
                  {notes.length === 0 && (
                    <div className="text-center text-gray-500 text-sm py-6">
                      No notes yet.
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="chat">
          <AdminChat
            clientId={clientId}
            clientName={`${profile.first_name || ""} ${profile.last_name || ""}`}
            clientInitials={`${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`}
          />
        </TabsContent>

        {/* Upload File dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogScrollContent className="bg-white text-gray-900 border border-gray-200 max-w-md">
            <DialogHeader className="shrink-0">
              <DialogTitle>Upload file</DialogTitle>
              <DialogDescription>
                Attach a file to this client.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4 pt-2">
              <div className="space-y-1">
                <Label htmlFor="upload-name" className="text-sm text-gray-800">
                  File name
                </Label>
                <Input
                  id="upload-name"
                  placeholder="e.g. Program – Week 1"
                  value={uploadName}
                  onChange={(e) => setUploadName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="upload-file" className="text-sm text-gray-800">
                  File
                </Label>
                <Input
                  id="upload-file"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    setUploadFile(file);
                    if (file && !uploadName.trim()) {
                      setUploadName(file.name);
                    }
                  }}
                />
                {uploadFile && (
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: <span className="font-medium">{uploadFile.name}</span>
                  </p>
                )}
              </div>
            </DialogBody>
            <DialogFooter className="shrink-0">
              <Button
                variant="outline"
                onClick={() => {
                  setUploadDialogOpen(false);
                  setUploadFile(null);
                  setUploadName("");
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={!uploadFile || !uploadName.trim() || filesUploading}
                onClick={async () => {
                  if (!uploadFile) return;
                  setFilesUploading(true);
                  try {
                    const fileType = uploadFile.type?.startsWith("image/") ? "image" : "file";
                    await adminUploadClientFile(clientId, uploadFile, fileType, uploadName);
                    await refreshClientFiles();
                    setUploadDialogOpen(false);
                    setUploadFile(null);
                    setUploadName("");
                  } finally {
                    setFilesUploading(false);
                  }
                }}
              >
                {filesUploading ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogScrollContent>
        </Dialog>

        {/* Add / Edit Note dialog */}
        <Dialog
          open={noteDialogOpen}
          onOpenChange={(open) => {
            if (!open && noteDialogSaving) return;
            setNoteDialogOpen(open);
            if (!open) {
              setNoteDialogText("");
              setNoteDialogIsPrivate(false);
              setEditingNoteId(null);
              setNoteDialogMode("create");
            }
          }}
        >
          <DialogScrollContent className="bg-white text-gray-900 border border-gray-200 max-w-md">
            <DialogHeader className="shrink-0">
              <DialogTitle>{noteDialogMode === "create" ? "Add note" : "Edit note"}</DialogTitle>
              <DialogDescription>
                {noteDialogIsPrivate
                  ? "Private notes are only visible to admins and trainers."
                  : "Shared notes are visible to the client."}
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="note-text" className="text-sm text-gray-800">
                  Note
                </Label>
                <Textarea
                  id="note-text"
                  rows={4}
                  placeholder="Write a note about this client..."
                  value={noteDialogText}
                  onChange={(e) => setNoteDialogText(e.target.value)}
                  disabled={noteDialogSaving}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="note-private"
                  checked={noteDialogIsPrivate}
                  onCheckedChange={(checked) => setNoteDialogIsPrivate(checked === true)}
                  disabled={noteDialogSaving}
                />
                <Label
                  htmlFor="note-private"
                  className="text-sm font-normal cursor-pointer text-gray-800"
                >
                  Private
                </Label>
              </div>
            </DialogBody>
            <DialogFooter className="shrink-0">
              <Button
                variant="outline"
                disabled={noteDialogSaving}
                onClick={() => {
                  setNoteDialogOpen(false);
                  setNoteDialogText("");
                  setNoteDialogIsPrivate(false);
                  setEditingNoteId(null);
                  setNoteDialogMode("create");
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-primary text-black hover:bg-primary/90"
                disabled={!noteDialogText.trim() || noteDialogSaving}
                onClick={handleSaveNote}
              >
                {noteDialogSaving ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </DialogScrollContent>
        </Dialog>

        <AlertDialog open={Boolean(noteToDelete)} onOpenChange={(open) => !open && setNoteToDelete(null)}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete note?</AlertDialogTitle>
              <AlertDialogDescription>
                This note will be permanently removed. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={noteDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={noteDeleting}
                onClick={(e) => {
                  e.preventDefault();
                  void handleDeleteNote();
                }}
              >
                {noteDeleting ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Assign Workout Dialog */}
        <AssignWorkoutDialog
          open={assignWorkoutDialogOpen}
          onOpenChange={(open) => {
            setAssignWorkoutDialogOpen(open);
            if (!open) {
              setSelectedDate(null);
            }
          }}
          clientId={clientId}
          clientName={`${clientData.profile.first_name} ${clientData.profile.last_name}`}
          assignmentDate={selectedDate}
          onSelectTemplate={(assignedWorkoutId) => {
            if (onAssignWorkout) {
              onAssignWorkout(clientId, selectedDate, assignedWorkoutId);
            }
          }}
          onCreateFromScratch={() => {
            if (onAssignWorkout) {
              onAssignWorkout(clientId, selectedDate);
            }
          }}
          />

        <EditAssignedWorkoutWithAIDialog
          open={editWorkoutWithAIOpen}
          onOpenChange={(open) => {
            setEditWorkoutWithAIOpen(open);
            if (!open) {
              setWorkoutToEditWithAI(null);
            }
          }}
          clientId={clientId}
          workout={workoutToEditWithAI}
          onWorkoutUpdated={refreshWorkoutAssignments}
          onOpenManualEdit={(assignedWorkoutId, nextClientId, assignmentDate) => {
            if (onEditWorkout) {
              onEditWorkout(assignedWorkoutId, nextClientId, assignmentDate);
            }
          }}
        />

        {/* Delete Workout Confirmation Dialog */}
        <Dialog open={deleteWorkoutDialogOpen} onOpenChange={setDeleteWorkoutDialogOpen}>
          <DialogContent className="bg-white text-black">
            <DialogHeader>
              <DialogTitle>Delete Workout</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{workoutToDelete?.name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteWorkoutDialogOpen(false);
                  setWorkoutToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  if (workoutToDelete) {
                    try {
                      await deleteWorkoutAssigned(workoutToDelete.id);
                      // Refresh workout list
                      const assignments = await listWorkoutAssigned(clientId);
                      setWorkoutAssignments(assignments);
                      toast.success("Workout deleted", {
                        description: `"${workoutToDelete.name}" has been deleted.`,
                      });
                      if (onDeleteWorkout) {
                        onDeleteWorkout(workoutToDelete.id);
                      }
                    } catch (error: any) {
                      console.error("Failed to delete workout", error);
                      toast.error("Error deleting workout", {
                        description: error?.message || "Failed to delete workout",
                      });
                    }
                  }
                  setDeleteWorkoutDialogOpen(false);
                  setWorkoutToDelete(null);
                }}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Tabs>
    </div>
  );
};

export default ClientProfileView;


